import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Save, RefreshCw, LogOut, Plus, Trash2, X, Edit3, Users, BarChart3, Tag, Inbox, Globe, ExternalLink, FileText, Sparkles, MessageCircle, Mail, Phone, ShoppingCart, Boxes, ShieldCheck, Bell, BellOff, MousePointerClick, Repeat, MapPin, CalendarDays, Star, ClipboardList, Download, Building2, Send, Ban, Check, ChevronDown, ChevronLeft, ChevronRight, Map as MapIcon, Upload, Loader2 } from "lucide-react";
import AuthLogin from "@/components/AuthLogin";
import ImageUpload from "@/components/ImageUpload";
import PhotoGalleryEditor from "@/components/PhotoGalleryEditor";
import Lightbox from "@/components/Lightbox";
import { authApi, api, getToken, clearToken, imgUrl, uploadImage } from "@/lib/api";
import { resetTileConfig } from "@/lib/mapTiles";
import { pushSupported, getPushState, enablePush, disablePush, sendTestPush } from "@/lib/push";

const NUM = (v) => (v === "" || v === null ? 0 : Number(v));
const fmt = (n) => Math.round(n || 0).toLocaleString("sk-SK") + " €";

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("stats");
  const [pricing, setPricing] = useState(null);
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [allDistricts, setAllDistricts] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [seoSummary, setSeoSummary] = useState(null);
  const [blog, setBlog] = useState([]);
  const [blogSettings, setBlogSettings] = useState({ auto_blog: false, per_day: 3 });
  const [applications, setApplications] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [visualizations, setVisualizations] = useState([]);
  const [coupons, setCoupons] = useState(null);
  const [swatchUrl, setSwatchUrl] = useState("");
  const [visLimits, setVisLimits] = useState({ per_email_limit: 3, per_ip_limit: 8 });
  const [botVisits, setBotVisits] = useState(null);
  const [newsletter, setNewsletter] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [insOrders, setInsOrders] = useState([]);
  const [firms, setFirms] = useState([]);
  const [firmDistricts, setFirmDistricts] = useState({ SK: [], CZ: [] });
  const [editing, setEditing] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const cli = authApi();
      const me = await api.get("/auth/me", { headers: { Authorization: `Bearer ${getToken()}` } });
      if (me.data.role !== "admin") { clearToken(); setAuthed(false); return; }
      const [p, l, pa, st, d, kw, ss, bl, bs, ap, pr, or, ad, viz, cp, vc, bv, nl, bk, bd, rv, io] = await Promise.all([
        cli.get("/admin/pricing"), cli.get("/admin/leads"), cli.get("/admin/partners"),
        cli.get("/admin/stats"), cli.get("/admin/districts"), cli.get("/admin/keywords"),
        cli.get("/admin/seo/summary"), cli.get("/admin/blog"), cli.get("/admin/blog/settings"),
        cli.get("/admin/partner-applications"), cli.get("/admin/products"), cli.get("/admin/orders"),
        cli.get("/admin/admins"),
        cli.get("/admin/visualizations"), cli.get("/admin/coupons"), cli.get("/visualize/config"),
        cli.get("/admin/bot-visits"), cli.get("/admin/newsletter"), cli.get("/admin/bookings"),
        cli.get("/booking/blocked-dates"), cli.get("/admin/reviews"), cli.get("/admin/insulation-orders"),
      ]);      setPricing(p.data); setLeads(l.data); setPartners(pa.data); setStats(st.data);
      setAllDistricts(d.data); setKeywords(kw.data); setSeoSummary(ss.data);
      setBlog(bl.data); setBlogSettings(bs.data); setApplications(ap.data);
      setProducts(pr.data); setOrders(or.data); setAdmins(ad.data);
      setVisualizations(viz.data); setCoupons(cp.data); setSwatchUrl(vc.data?.swatch_url || "");
      setVisLimits({ per_email_limit: vc.data?.per_email_limit ?? 3, per_ip_limit: vc.data?.per_ip_limit ?? 8 });
      setBotVisits(bv.data);
      setNewsletter(nl.data);
      setBookings(bk.data);
      setBlockedDates(bd.data?.dates || []);
      setReviews(rv.data);
      setInsOrders(io.data);
      const [fr, fd] = await Promise.all([cli.get("/admin/lead-firms"), cli.get("/admin/lead-firms/districts")]);
      setFirms(fr.data); setFirmDistricts(fd.data);
      setAuthed(true);
    } catch { clearToken(); setAuthed(false); }
  };

  useEffect(() => { if (getToken()) load(); }, []);
  const logout = () => { clearToken(); setAuthed(false); };

  const saveSwatch = async () => {
    try {
      await authApi().post("/admin/visualize/settings", {
        swatch_url: swatchUrl,
        per_email_limit: Number(visLimits.per_email_limit) || 3,
        per_ip_limit: Number(visLimits.per_ip_limit) || 8,
      });
      toast.success("Nastavenia vizualizácie uložené");
    } catch { toast.error("Uloženie zlyhalo"); }
  };

  const setGalleryStatus = async (id, action) => {
    try {
      const { data } = await authApi().post(`/admin/visualizations/${id}/gallery`, { action });
      setVisualizations((vs) => vs.map((x) => (x.id === id ? { ...x, gallery_status: data.gallery_status } : x)));
      toast.success(action === "approve" ? "Vizualizácia je vo verejnej galérii" : "Vizualizácia zamietnutá / stiahnutá z galérie");
    } catch (e) { toast.error(e?.response?.data?.detail || "Akcia zlyhala"); }
  };

  const savePricing = async () => {
    setSaving(true);
    try { await authApi().put("/admin/pricing", pricing); toast.success("Cenník uložený"); }
    catch { toast.error("Uloženie zlyhalo"); } finally { setSaving(false); }
  };
  const setLeadStatus = async (id, status) => {
    try { await authApi().put(`/admin/leads/${id}`, { status }); setLeads((ls) => ls.map((x) => (x.id === id ? { ...x, status } : x))); }
    catch { toast.error("Chyba"); }
  };
  const savePartner = async (partner) => {
    try {
      const cli = authApi();
      if (partner.id) await cli.put(`/admin/partners/${partner.id}`, partner);
      else await cli.post("/admin/partners", partner);
      toast.success("Partner uložený"); setEditing(null);
      const [pa, d] = await Promise.all([cli.get("/admin/partners"), cli.get("/admin/districts")]);
      setPartners(pa.data); setAllDistricts(d.data);
    } catch { toast.error("Uloženie partnera zlyhalo"); }
  };
  const deletePartner = async (id) => {
    if (!window.confirm("Zmazať partnera?")) return;
    try { await authApi().delete(`/admin/partners/${id}`); setPartners((ps) => ps.filter((p) => p.id !== id)); toast.success("Zmazané"); }
    catch { toast.error("Chyba"); }
  };
  const togglePartnerActive = async (p) => {
    if (!p.active && (!p.districts || p.districts.length === 0)) {
      toast.error("Priraďte partnerovi aspoň jeden okres cez tlačidlo Upraviť, aby videl dopyty.");
      return;
    }
    try {
      await authApi().put(`/admin/partners/${p.id}/active`, { active: !p.active });
      setPartners((ps) => ps.map((x) => x.id === p.id ? { ...x, active: !x.active } : x));
      toast.success(!p.active ? "Partner schválený a aktivovaný" : "Partner deaktivovaný");
    } catch { toast.error("Chyba"); }
  };
  const togglePartnerFeatured = async (p) => {
    try {
      await authApi().put(`/admin/partners/${p.id}/active`, { active: p.active, featured: !p.featured });
      setPartners((ps) => ps.map((x) => x.id === p.id ? { ...x, featured: !x.featured } : x));
      toast.success(!p.featured ? `${p.company || p.name} je Top partner` : "Top partner zrušený");
    } catch { toast.error("Chyba"); }
  };
  const reloadSeo = async () => {
    const cli = authApi();
    const [kw, ss] = await Promise.all([cli.get("/admin/keywords"), cli.get("/admin/seo/summary")]);
    setKeywords(kw.data); setSeoSummary(ss.data);
  };
  const saveKeyword = async (k) => {
    try { const cli = authApi(); if (k.id) await cli.put(`/admin/keywords/${k.id}`, k); else await cli.post("/admin/keywords", k); toast.success("Uložené"); await reloadSeo(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Chyba"); }
  };
  const deleteKeyword = async (id) => { if (!window.confirm("Zmazať?")) return; try { await authApi().delete(`/admin/keywords/${id}`); await reloadSeo(); } catch { toast.error("Chyba"); } };
  const importTowns = async (csv) => {
    try { const { data } = await authApi().post("/admin/towns/import", { csv }); await reloadSeo(); toast.success(`+${data.added}, upravené ${data.updated}, spolu ${data.total}`); }
    catch { toast.error("Import zlyhal"); }
  };
  const reloadBlog = async () => { const cli = authApi(); const [bl, ss] = await Promise.all([cli.get("/admin/blog"), cli.get("/admin/seo/summary")]); setBlog(bl.data); setSeoSummary(ss.data); };
  const generateBlog = async (topic) => {
    toast.info("Generujem článok…");
    try { await authApi().post("/admin/blog/generate", { topic: topic || "" }); await reloadBlog(); toast.success("Článok vygenerovaný a publikovaný"); }
    catch { toast.error("Generovanie zlyhalo"); }
  };
  const toggleBlog = async (post) => { try { await authApi().put(`/admin/blog/${post.id}`, { published: !post.published }); setBlog((b) => b.map((x) => x.id === post.id ? { ...x, published: !x.published } : x)); } catch { toast.error("Chyba"); } };
  const deleteBlog = async (id) => { if (!window.confirm("Zmazať článok?")) return; try { await authApi().delete(`/admin/blog/${id}`); setBlog((b) => b.filter((x) => x.id !== id)); } catch { toast.error("Chyba"); } };
  const saveBlogSettings = async (s) => { try { const { data } = await authApi().put("/admin/blog/settings", s); setBlogSettings(data); toast.success("Nastavenia uložené"); } catch { toast.error("Chyba"); } };
  const saveBlogPost = async (post) => {
    try {
      await authApi().put(`/admin/blog/${post.id}`, post);
      toast.success("Článok uložený"); setEditingBlog(null);
      const { data } = await authApi().get("/admin/blog"); setBlog(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Uloženie zlyhalo"); }
  };
  const setAppStatus = async (id, status) => { try { await authApi().put(`/admin/partner-applications/${id}`, { status }); setApplications((a) => a.map((x) => x.id === id ? { ...x, status } : x)); } catch { toast.error("Chyba"); } };

  const saveProduct = async (prod) => {
    try {
      const cli = authApi();
      if (prod.id) await cli.put(`/admin/products/${prod.id}`, prod);
      else await cli.post("/admin/products", prod);
      toast.success("Produkt uložený"); setEditingProduct(null);
      const pr = await cli.get("/admin/products"); setProducts(pr.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Uloženie zlyhalo"); }
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Zmazať produkt?")) return;
    try { await authApi().delete(`/admin/products/${id}`); setProducts((ps) => ps.filter((p) => p.id !== id)); toast.success("Zmazané"); }
    catch { toast.error("Chyba"); }
  };
  const setOrderStatus = async (id, status) => { try { await authApi().put(`/admin/orders/${id}`, { status }); setOrders((os) => os.map((x) => x.id === id ? { ...x, status } : x)); } catch { toast.error("Chyba"); } };
  const setBookingStatus = async (id, status) => { try { await authApi().patch(`/admin/bookings/${id}`, { status }); setBookings((bs) => bs.map((x) => x.id === id ? { ...x, status } : x)); } catch { toast.error("Chyba"); } };
  const deleteBooking = async (id) => {
    if (!window.confirm("Naozaj zmazať túto rezerváciu? Akcia sa nedá vrátiť.")) return;
    try { await authApi().delete(`/admin/bookings/${id}`); setBookings((bs) => bs.filter((x) => x.id !== id)); toast.success("Rezervácia zmazaná."); }
    catch { toast.error("Nepodarilo sa zmazať rezerváciu."); }
  };
  const saveBlockedDates = async (dates) => {
    try { const { data } = await authApi().post("/admin/blocked-dates", { dates }); setBlockedDates(data.dates || []); toast.success("Blokované termíny uložené."); }
    catch { toast.error("Nepodarilo sa uložiť termíny."); }
  };
  const createReview = async (r) => {
    try { const { data } = await authApi().post("/admin/reviews", r); setReviews((rs) => [data.review, ...rs]); toast.success("Recenzia pridaná."); return true; }
    catch { toast.error("Nepodarilo sa pridať recenziu."); return false; }
  };
  const deleteReview = async (id) => {
    if (!window.confirm("Zmazať túto recenziu?")) return;
    try { await authApi().delete(`/admin/reviews/${id}`); setReviews((rs) => rs.filter((x) => x.id !== id)); toast.success("Recenzia zmazaná."); }
    catch { toast.error("Nepodarilo sa zmazať recenziu."); }
  };
  const approveReview = async (id, approved) => {
    try { await authApi().patch(`/admin/reviews/${id}`, { approved }); setReviews((rs) => rs.map((x) => x.id === id ? { ...x, approved } : x)); toast.success(approved ? "Recenzia zverejnená." : "Recenzia skrytá."); }
    catch { toast.error("Zmena sa nepodarila."); }
  };
  const setInsOrderStatus = async (id, status, message = "", sendEmail = true) => {
    try { await authApi().patch(`/admin/insulation-orders/${id}`, { status, message, send_email: sendEmail }); setInsOrders((os) => os.map((x) => x.id === id ? { ...x, status } : x)); }
    catch { toast.error("Chyba"); }
  };
  const deleteInsOrder = async (id) => {
    if (!window.confirm("Zmazať túto objednávku?")) return;
    try { await authApi().delete(`/admin/insulation-orders/${id}`); setInsOrders((os) => os.filter((x) => x.id !== id)); toast.success("Objednávka zmazaná."); }
    catch { toast.error("Nepodarilo sa zmazať objednávku."); }
  };
  const reloadFirms = async () => { const { data } = await authApi().get("/admin/lead-firms"); setFirms(data); };
  const createFirm = async (body) => {
    try { await authApi().post("/admin/lead-firms", body); await reloadFirms(); toast.success("Firma pridaná."); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || "Nepodarilo sa pridať firmu."); return false; }
  };
  const bulkFirms = async (items) => {
    try { const { data } = await authApi().post("/admin/lead-firms/bulk", items); await reloadFirms(); toast.success(`Pridané: ${data.added}, preskočené (duplicita): ${data.skipped}`); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || "Import zlyhal."); return false; }
  };
  const updateFirm = async (id, patch) => {
    try { await authApi().patch(`/admin/lead-firms/${id}`, patch); setFirms((fs) => fs.map((x) => x.id === id ? { ...x, ...patch } : x)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Zmena zlyhala."); }
  };
  const deleteFirm = async (id) => {
    if (!window.confirm("Zmazať túto firmu?")) return;
    try { await authApi().delete(`/admin/lead-firms/${id}`); setFirms((fs) => fs.filter((x) => x.id !== id)); toast.success("Firma zmazaná."); }
    catch { toast.error("Nepodarilo sa zmazať firmu."); }
  };
  const grantAdmin = async (body) => {
    try { const { data } = await authApi().post("/admin/admins", body); toast.success(data.message || "Hotovo"); const ad = await authApi().get("/admin/admins"); setAdmins(ad.data); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || "Chyba"); return false; }
  };
  const revokeAdmin = async (id) => {
    if (!window.confirm("Odobrať práva administrátora?")) return;
    try { await authApi().delete(`/admin/admins/${id}`); setAdmins((a) => a.filter((x) => x.id !== id)); toast.success("Práva odobraté"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Chyba"); }
  };

  if (!authed) return <AuthLogin role="admin" title="Admin" onSuccess={() => load()} />;

  const tabs = [["stats", "Štatistika", BarChart3], ["calcs", "Výpočty", MousePointerClick], ["pricing", "Cenník", Tag], ["products", `Príslušenstvo (${products.length})`, Boxes], ["orders", `Objednávky (${orders.length})`, ShoppingCart], ["objednavky", `Objednávky zateplenia (${insOrders.length})`, ClipboardList], ["firmy", `Firmy – dopyty (${firms.length})`, Building2], ["bookings", `Obhliadky (${bookings.length})`, CalendarDays], ["reviews", `Recenzie (${reviews.length})`, Star], ["partners", `Partneri (${partners.length})`, Users], ["seo", "SEO", Globe], ["blog", `Blog (${blog.length})`, FileText], ["applications", `Žiadosti (${applications.length})`, Inbox], ["leads", `Dopyty (${leads.length})`, Inbox], ["visualizations", `Vizualizácie (${visualizations.length})`, Sparkles], ["coupons", `Kupóny`, Tag], ["botvisits", `Crawlery`, Globe], ["ema", `Ema chat`, MessageCircle], ["subscribers", `Odberatelia`, Mail], ["admins", `Administrátori (${admins.length})`, ShieldCheck]];

  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="border-b border-white/10 px-5 md:px-10 h-20 flex items-center justify-between sticky top-0 bg-ink/90 backdrop-blur z-10">
        <div className="font-display text-3xl">ZATEPLENIA<span className="text-gold">.EU</span> <span className="text-white/40 text-lg">/ admin</span></div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="p-2 border border-white/20 hover:border-white/50"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-white/50 text-sm"><LogOut className="w-4 h-4" /> Odhlásiť</button>
        </div>
      </header>

      <div className="px-5 md:px-10 py-8 max-w-[1400px] mx-auto">
        {/* Modern scrollable tab bar: wheel converts to horizontal scroll, edge arrows, active tab auto-centres */}
        <AdminTabBar tabs={tabs} tab={tab} setTab={setTab} />

        {tab === "stats" && stats && <StatsTab stats={stats} />}
        {tab === "calcs" && <CalcsTab />}
        {tab === "pricing" && pricing && <PricingTab pricing={pricing} setPricing={setPricing} save={savePricing} saving={saving} />}
        {tab === "products" && <ProductsTab products={products} onAdd={() => setEditingProduct({})} onEdit={setEditingProduct} onDelete={deleteProduct} />}
        {tab === "orders" && <OrdersTab orders={orders} onStatus={setOrderStatus} />}
        {tab === "objednavky" && <InsulationOrdersTab orders={insOrders} onStatus={setInsOrderStatus} onDelete={deleteInsOrder} />}
        {tab === "firmy" && <LeadFirmsTab firms={firms} districts={firmDistricts} onCreate={createFirm} onBulk={bulkFirms} onUpdate={updateFirm} onDelete={deleteFirm} />}
        {tab === "bookings" && <BookingsTab bookings={bookings} onStatus={setBookingStatus} onDelete={deleteBooking} blockedDates={blockedDates} onSaveBlocked={saveBlockedDates} />}
        {tab === "reviews" && <ReviewsTab reviews={reviews} onCreate={createReview} onDelete={deleteReview} onApprove={approveReview} />}
        {tab === "partners" && <PartnersTab partners={partners} onAdd={() => setEditing({})} onEdit={setEditing} onDelete={deletePartner} onToggleActive={togglePartnerActive} onToggleFeatured={togglePartnerFeatured} />}
        {tab === "seo" && <SeoTab summary={seoSummary} keywords={keywords} onSave={saveKeyword} onDelete={deleteKeyword} onImport={importTowns} />}
        {tab === "blog" && <BlogTab blog={blog} settings={blogSettings} onGenerate={generateBlog} onToggle={toggleBlog} onDelete={deleteBlog} onSaveSettings={saveBlogSettings} onEdit={setEditingBlog} />}
        {tab === "applications" && <ApplicationsTab apps={applications} onStatus={setAppStatus} />}
        {tab === "leads" && <LeadsTab leads={leads} setLeadStatus={setLeadStatus} onLeadUpdate={(nl) => setLeads((ls) => ls.map((x) => (x.id === nl.id ? nl : x)))} />}
        {tab === "visualizations" && <VisualizationsTab items={visualizations} swatchUrl={swatchUrl} setSwatchUrl={setSwatchUrl} visLimits={visLimits} setVisLimits={setVisLimits} onSaveSwatch={saveSwatch} onGallery={setGalleryStatus} />}
        {tab === "coupons" && <CouponsTab data={coupons} />}
        {tab === "botvisits" && <BotVisitsTab data={botVisits} />}
        {tab === "ema" && <EmaTab />}
        {tab === "subscribers" && <SubscribersTab data={newsletter} />}
        {tab === "admins" && <AdminsTab admins={admins} onGrant={grantAdmin} onRevoke={revokeAdmin} meEmail={""} />}
      </div>

      {editing !== null && <PartnerEditor partner={editing} allDistricts={allDistricts} onClose={() => setEditing(null)} onSave={savePartner} />}
      {editingProduct !== null && <ProductEditor product={editingProduct} onClose={() => setEditingProduct(null)} onSave={saveProduct} vatPct={pricing?.vat_pct ?? 23} />}
      {editingBlog !== null && <BlogEditor post={editingBlog} onClose={() => setEditingBlog(null)} onSave={saveBlogPost} />}
    </div>
  );
}

function PushControls() {
  const [state, setState] = useState({ supported: true, subscribed: false, permission: "default" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) { setState({ supported: false, subscribed: false, permission: "unsupported" }); return; }
    getPushState().then(setState).catch(() => {});
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (state.subscribed) {
        await disablePush();
        toast.success("Notifikácie vypnuté na tomto zariadení");
      } else {
        await enablePush();
        toast.success("Notifikácie zapnuté — budete dostávať upozornenia na toto zariadenie");
      }
      setState(await getPushState());
    } catch (e) {
      toast.error(e.message || "Chyba pri nastavení notifikácií");
    } finally { setBusy(false); }
  };

  const test = async () => {
    try { await sendTestPush(); toast.success("Testovacia notifikácia odoslaná"); }
    catch { toast.error("Test zlyhal"); }
  };

  return (
    <section className="border border-gold/30 bg-ink-2 p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="push-controls">
      <div className="flex items-center gap-4 flex-1">
        {state.subscribed ? <Bell className="w-8 h-8 text-gold" /> : <BellOff className="w-8 h-8 text-white/40" />}
        <div>
          <div className="font-display text-2xl text-white">Notifikácie na telefón</div>
          <div className="text-white/50 text-sm">
            {!state.supported
              ? "Tento prehliadač nepodporuje push notifikácie. Nainštalujte appku na plochu telefónu."
              : state.subscribed
                ? "Zapnuté — dostanete upozornenie pri novom dopyte, ponuke aj vizualizácii."
                : "Zapnite si upozornenia na nové dopyty, ponuky a vizualizácie priamo na toto zariadenie."}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {state.subscribed && (
          <button onClick={test} data-testid="push-test-btn" className="px-4 py-2 border border-white/20 text-white/70 hover:border-gold/50 text-sm">Test</button>
        )}
        <button onClick={toggle} disabled={busy || !state.supported} data-testid="push-toggle-btn"
          className={`px-5 py-2 font-bold uppercase tracking-wider text-sm flex items-center gap-2 ${state.subscribed ? "border border-white/20 text-white/70 hover:border-red-400/50" : "bg-gold text-ink hover:bg-gold-light"} ${(busy || !state.supported) ? "opacity-50" : ""}`}>
          {state.subscribed ? <><BellOff className="w-4 h-4" /> Vypnúť</> : <><Bell className="w-4 h-4" /> Zapnúť</>}
        </button>
      </div>
    </section>
  );
}

function LiveNow() {
  const [live, setLive] = useState({ count: 0, pages: [] });
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try { const { data } = await authApi().get("/admin/live"); if (alive) setLive(data); }
      catch { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const PATH_LABEL = (p) => {
    if (p === "/") return "Domov";
    if (p === "/vizualizacia") return "Vizualizácia";
    if (p === "/blog") return "Blog";
    if (p === "/partneri") return "Partneri";
    if (p.startsWith("/blog/")) return "Článok";
    return p;
  };

  return (
    <section className="border border-white/10 bg-ink-2 p-6" data-testid="live-now">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span className={`absolute inline-flex h-full w-full rounded-full ${live.count ? "bg-green-400 animate-ping" : "bg-white/20"} opacity-75`} />
            <span className={`relative inline-flex rounded-full h-3 w-3 ${live.count ? "bg-green-400" : "bg-white/30"}`} />
          </span>
          <div className="font-mono2 text-xs uppercase tracking-widest text-white/50">Naživo teraz</div>
        </div>
        <div className="font-display text-5xl text-gold leading-none" data-testid="live-count">{live.count}</div>
      </div>
      {live.pages && live.pages.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {live.pages.map((p) => (
            <span key={p.path} className="text-xs font-mono2 text-white/60 border border-white/10 px-2 py-1">
              {PATH_LABEL(p.path)} · <span className="text-gold">{p.count}</span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function NotifSettingsCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { authApi().get("/admin/notif-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const save = async () => {
    setSaving(true);
    try { const { data } = await authApi().post("/admin/notif-settings", s); setS(data); toast.success("Nastavenia notifikácií uložené"); }
    catch { toast.error("Uloženie zlyhalo"); } finally { setSaving(false); }
  };
  const Toggle = ({ on, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid} role="switch" aria-checked={on}
      className={`w-12 h-7 rounded-full relative transition-colors ${on ? "bg-gold" : "bg-white/15"}`}>
      <span className={`absolute top-1 w-5 h-5 rounded-full bg-ink transition-all ${on ? "left-6" : "left-1"}`} />
    </button>
  );
  const hours = Array.from({ length: 24 }, (_, i) => i);
  return (
    <section className="border border-white/10 bg-ink-2 p-6 space-y-5" data-testid="notif-settings">
      <div className="font-mono2 text-xs uppercase tracking-widest text-gold">Nastavenie notifikácií</div>

      <div className="flex items-center justify-between">
        <div>
          <div className="text-white font-semibold">Tichý čas</div>
          <div className="text-white/50 text-sm">Nočné notifikácie sa odložia na ráno.</div>
        </div>
        <Toggle on={s.quiet_enabled} testid="quiet-toggle" onClick={() => setS({ ...s, quiet_enabled: !s.quiet_enabled })} />
      </div>

      {s.quiet_enabled && (
        <div className="flex items-center gap-4 pl-1">
          <label className="text-white/60 text-sm flex items-center gap-2">Od
            <select value={s.quiet_start} data-testid="quiet-start" onChange={(e) => setS({ ...s, quiet_start: Number(e.target.value) })}
              className="bg-ink border border-white/20 px-2 py-1 text-sm text-white">
              {hours.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
            </select>
          </label>
          <label className="text-white/60 text-sm flex items-center gap-2">Do
            <select value={s.quiet_end} data-testid="quiet-end" onChange={(e) => setS({ ...s, quiet_end: Number(e.target.value) })}
              className="bg-ink border border-white/20 px-2 py-1 text-sm text-white">
              {hours.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
            </select>
          </label>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-white/10 pt-5">
        <div>
          <div className="text-white font-semibold">Tichý režim cez víkend</div>
          <div className="text-white/50 text-sm">V sobotu a nedeľu platia vlastné hodiny ticha — leady vás nezobudia v nedeľu ráno.</div>
        </div>
        <Toggle on={s.weekend_enabled !== false} testid="weekend-toggle" onClick={() => setS({ ...s, weekend_enabled: !(s.weekend_enabled !== false) })} />
      </div>

      {s.weekend_enabled !== false && (
        <div className="flex items-center gap-4 pl-1">
          <label className="text-white/60 text-sm flex items-center gap-2">Od
            <select value={s.weekend_start ?? 20} data-testid="weekend-start" onChange={(e) => setS({ ...s, weekend_start: Number(e.target.value) })}
              className="bg-ink border border-white/20 px-2 py-1 text-sm text-white">
              {hours.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
            </select>
          </label>
          <label className="text-white/60 text-sm flex items-center gap-2">Do
            <select value={s.weekend_end ?? 9} data-testid="weekend-end" onChange={(e) => setS({ ...s, weekend_end: Number(e.target.value) })}
              className="bg-ink border border-white/20 px-2 py-1 text-sm text-white">
              {hours.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
            </select>
          </label>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-white/10 pt-5">
        <div>
          <div className="text-white font-semibold">Upozornenie na špičku návštev</div>
          <div className="text-white/50 text-sm">Notifikácia keď je naraz na webe veľa ľudí.</div>
        </div>
        <Toggle on={s.spike_enabled} testid="spike-toggle" onClick={() => setS({ ...s, spike_enabled: !s.spike_enabled })} />
      </div>

      {s.spike_enabled && (
        <label className="text-white/60 text-sm flex items-center gap-2 pl-1">Prah (počet ľudí naraz)
          <input type="number" min="2" value={s.spike_threshold} data-testid="spike-threshold"
            onChange={(e) => setS({ ...s, spike_threshold: Number(e.target.value) })}
            className="bg-ink border border-white/20 px-2 py-1 text-sm text-white w-20" />
        </label>
      )}

      <div className="border-t border-white/10 pt-5 space-y-3">
        <div>
          <div className="text-white font-semibold">Typy notifikácií</div>
          <div className="text-white/50 text-sm">Vyberte, ktoré udalosti majú posielať push notifikáciu. Vypnuté udalosti sa stále zobrazia v Centre diania.</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {(s.types || []).map((t) => (
            <div key={t.key} className="flex items-center justify-between gap-3">
              <div className="text-white/70 text-sm">{t.label}</div>
              <Toggle on={(s.toggles || {})[t.key] !== false} testid={`notif-toggle-${t.key}`}
                onClick={() => setS({ ...s, toggles: { ...(s.toggles || {}), [t.key]: (s.toggles || {})[t.key] === false } })} />
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="notif-save"
        className="bg-gold text-ink px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-gold-light disabled:opacity-50">
        {saving ? "Ukladám…" : "Uložiť nastavenia"}
      </button>
    </section>
  );
}

function ActivityFeed() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => authApi().get("/admin/events?limit=40")
    .then((r) => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  return (
    <section className="border border-white/10 bg-ink-2 p-6" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold">Centrum diania</div>
          <div className="text-white/50 text-sm mt-1">Všetky udalosti na webe v chronologickom poradí.</div>
        </div>
        <button onClick={load} data-testid="activity-refresh-btn" aria-label="Obnoviť"
          className="text-white/40 hover:text-gold transition-colors"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {loading ? (
        <div className="text-white/40 text-sm">Načítavam…</div>
      ) : events.length === 0 ? (
        <div className="text-white/40 text-sm" data-testid="activity-empty">Zatiaľ žiadne udalosti.</div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
          {events.map((e) => (
            <div key={e.id} className="flex gap-3 border-b border-white/5 pb-3" data-testid={`activity-item-${e.id}`}>
              <div className="text-white/30 text-xs font-mono2 whitespace-nowrap pt-0.5 w-20 shrink-0">
                {new Date(e.created_at).toLocaleString("sk-SK", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold">{e.title}</div>
                <div className="text-white/50 text-sm break-words">{e.body}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const CALC_MODE_LABELS = { rychly: "Rýchly", precise: "Presný režim", ai: "AI z pôdorysu" };
const CALC_MATERIAL_LABELS = { eps: "EPS biely", eps_white: "EPS biely", eps_grafit: "EPS grafitový", eps_graphite: "EPS grafitový", mineral: "Minerálna vata", mineralna: "Minerálna vata" };

function CalcsTab() {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    setData(null);
    authApi().get("/admin/calculations", { params: { mode, status } })
      .then((r) => setData(r.data))
      .catch(() => { setData({ items: [], stats: null }); toast.error("Načítanie výpočtov zlyhalo."); });
  }, [mode, status]);

  if (!data) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-gold animate-spin" /></div>;
  const { items, stats } = data;
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("sk-SK", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso || "—"; }
  };
  return (
    <div data-testid="calcs-tab">
      <h2 className="font-display text-2xl text-white mb-1">Výpočty kalkulačky</h2>
      <p className="text-white/50 text-sm mb-5">Čo zákazníci počítali — vrátane opustených kalkulačiek a režimu (rýchly / presný / AI z pôdorysu).</p>

      {stats && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="calcs-stats">
          <span className="border border-white/15 px-3 py-1.5 text-xs text-white/70">Celkom: <b className="text-white">{stats.total}</b></span>
          <span className="border border-white/15 px-3 py-1.5 text-xs text-white/70">Odoslané dopyty: <b className="text-gold">{stats.converted}</b></span>
          <span className="border border-white/15 px-3 py-1.5 text-xs text-white/70">Opustené: <b className="text-white">{stats.total - stats.converted}</b></span>
          <span className="border border-white/15 px-3 py-1.5 text-xs text-white/70">AI z pôdorysu: <b className="text-gold">{stats.by_mode?.ai || 0}</b></span>
          <span className="border border-white/15 px-3 py-1.5 text-xs text-white/70">Presný režim: <b className="text-white">{stats.by_mode?.precise || 0}</b></span>
          <span className="border border-white/15 px-3 py-1.5 text-xs text-white/70">Rýchly: <b className="text-white">{stats.by_mode?.rychly || 0}</b></span>
        </div>
      )}

      {stats?.capture_ab && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="calcs-ab">
          {[["box", "Jemný box pod výsledkom"], ["gate", "Mäkká brána (rozmazané ponuky)"]].map(([v, label]) => {
            const s = stats.capture_ab[v] || {};
            return (
              <div key={v} className="border border-white/10 bg-ink-2 p-4" data-testid={`calcs-ab-${v}`}>
                <p className="text-white/40 text-[10px] font-mono2 uppercase tracking-widest mb-1">{v === "box" ? "Varianta A" : "Varianta B"} · {label}</p>
                <div className="flex items-end gap-4">
                  <div>
                    <p className="font-display text-2xl text-white">{s.captured ?? 0}<span className="text-white/40 text-sm"> / {s.shown ?? 0}</span></p>
                    <p className="text-white/40 text-[11px]">zachytených / zobrazených</p>
                  </div>
                  <p className={`font-display text-2xl ml-auto ${s.rate != null && s.rate >= 10 ? "text-gold" : "text-white/70"}`} data-testid={`calcs-ab-${v}-rate`}>
                    {s.rate != null ? `${s.rate} %` : "—"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="calcs-filter-mode"
          className="bg-ink-2 border border-white/15 text-white/80 text-xs px-3 py-2 focus:border-gold focus:outline-none">
          <option value="">Všetky režimy</option>
          <option value="rychly">Rýchly</option>
          <option value="precise">Presný režim</option>
          <option value="ai">AI z pôdorysu</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="calcs-filter-status"
          className="bg-ink-2 border border-white/15 text-white/80 text-xs px-3 py-2 focus:border-gold focus:outline-none">
          <option value="">Všetky stavy</option>
          <option value="odoslany">Odoslaný dopyt</option>
          <option value="opustena">Opustená</option>
        </select>
      </div>

      {items.length === 0 ? (
        <p className="text-white/40 text-sm" data-testid="calcs-empty">Žiadne výpočty pre zvolený filter.</p>
      ) : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-left text-sm" data-testid="calcs-table">
            <thead>
              <tr className="border-b border-white/10 text-white/40 text-[11px] font-mono2 uppercase tracking-widest">
                <th className="px-3 py-2">Dátum</th><th className="px-3 py-2">Okres</th><th className="px-3 py-2">Plocha</th>
                <th className="px-3 py-2">Materiál</th><th className="px-3 py-2">Hrúbka</th><th className="px-3 py-2">Cena</th>
                <th className="px-3 py-2">Režim</th><th className="px-3 py-2">Stav</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c, i) => (
                <tr key={c.id || i} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`calcs-row-${i}`}>
                  <td className="px-3 py-2 text-white/60 text-xs whitespace-nowrap">{fmtDate(c.created_at)}</td>
                  <td className="px-3 py-2 text-white/80">{c.district || "—"}</td>
                  <td className="px-3 py-2 text-white/80 whitespace-nowrap">{c.area} m²</td>
                  <td className="px-3 py-2 text-white/80">{CALC_MATERIAL_LABELS[c.material] || c.material || "—"}</td>
                  <td className="px-3 py-2 text-white/80 whitespace-nowrap">{c.thickness} mm</td>
                  <td className="px-3 py-2 text-gold font-mono2 whitespace-nowrap">{fmt(c.total)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${c.mode === "ai" ? "bg-gold text-ink" : c.mode === "precise" ? "border border-gold/50 text-gold" : "border border-white/20 text-white/50"}`}
                      data-testid={`calcs-mode-${i}`}>{CALC_MODE_LABELS[c.mode] || "Rýchly"}</span>
                  </td>
                  <td className="px-3 py-2">
                    {c.status === "odoslany"
                      ? <span className="text-emerald-400 text-xs font-semibold" data-testid={`calcs-status-${i}`}>Odoslaný dopyt</span>
                      : <span className="text-white/40 text-xs" data-testid={`calcs-status-${i}`}>Opustená</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminTabBar({ tabs, tab, setTab }) {
  const ref = useRef(null);

  // Centre the active tab whenever it changes.
  useEffect(() => {
    ref.current?.querySelector(`[data-testid="admin-tab-${tab}"]`)
      ?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [tab]);

  const scrollBy = (dir) => ref.current?.scrollBy({ left: dir * 340, behavior: "smooth" });

  // Vertical mouse wheel scrolls the bar horizontally (until its edge, then the page scrolls).
  const onWheel = (e) => {
    const el = ref.current;
    if (!el || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    const atStart = el.scrollLeft <= 0 && e.deltaY < 0;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 && e.deltaY > 0;
    if (atStart || atEnd) return;
    e.preventDefault();
    el.scrollLeft += e.deltaY;
  };

  return (
    <div className="relative mb-12 -mx-5 md:mx-0">
      <div ref={ref} onWheel={onWheel} data-testid="admin-tabs"
        className="flex gap-2 overflow-x-auto no-scrollbar px-5 md:px-0 pb-1 scroll-smooth">
        {tabs.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`shrink-0 flex items-center gap-2 px-4 py-2.5 font-mono2 text-xs uppercase tracking-widest border whitespace-nowrap transition-all ${tab === k ? "border-gold bg-gold text-ink shadow-[0_0_20px_rgba(212,175,55,0.25)]" : "border-white/15 bg-white/[0.03] text-white/60 hover:border-gold/60 hover:text-white"}`}>
            <Icon className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-ink to-transparent" />
      <div className="absolute -bottom-3 right-0 flex gap-1.5">
        <button onClick={() => scrollBy(-1)} data-testid="tabs-scroll-left" aria-label="Posunúť taby doľava"
          className="p-1.5 bg-ink border border-white/20 text-white/60 hover:border-gold hover:text-gold transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        <button onClick={() => scrollBy(1)} data-testid="tabs-scroll-right" aria-label="Posunúť taby doprava"
          className="p-1.5 bg-ink border border-white/20 text-white/60 hover:border-gold hover:text-gold transition-colors"><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

const QUICK_LINKS = [
  ["/", "Domov + kalkulačka"],
  ["/objednavka", "Objednávka zateplenia"],
  ["/rezervacia", "Rezervácia obhliadky"],
  ["/funnel/strach", "Lievik: Strach"],
  ["/funnel/istota", "Lievik: Istota"],
  ["/funnel/uspora", "Lievik: Úspora"],
  ["/zateplenie-fasady/bratislava", "Landing: Zateplenie fasády"],
  ["/zateplenie/bratislava", "Landing: Zateplenie"],
  ["/okresy", "Okresy (73)"],
  ["/okres/bratislava", "Okres: Bratislava"],
  ["/blog", "Blog"],
  ["/faq", "Časté otázky (FAQ)"],
  ["/vizualizacia", "AI Vizualizácia"],
  ["/galeria", "Galéria vizualizácií"],
  ["/realizatori", "Katalóg realizátorov"],
  ["/materialy", "Porovnanie materiálov"],
  ["/omietky", "Porovnanie omietok"],
  ["/normy", "Zákony a normy"],
  ["/kontakt", "Kontakt"],
  ["/partneri", "Naši partneri"],
  ["/stat-sa-partnerom", "Staňte sa partnerom"],
];

function QuickLinksCard() {
  return (
    <section className="border border-white/10 bg-ink-2 p-6" data-testid="quick-links">
      <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-1">Stránky a lieviky</div>
      <div className="text-white/50 text-sm mb-4">Rýchly prístup ku všetkým verejným stránkam — otvoria sa v novej záložke.</div>
      <div className="flex flex-wrap gap-2">
        {QUICK_LINKS.map(([href, label]) => (
          <a key={href} href={href} target="_blank" rel="noreferrer" data-testid={`quick-link-${href.replaceAll("/", "-").replace(/^-/, "") || "home"}`}
            className="group flex items-center gap-1.5 border border-white/10 px-3 py-2 text-sm text-white/60 hover:border-gold hover:text-gold transition-colors">
            {label} <ExternalLink className="w-3 h-3 text-white/30 group-hover:text-gold" />
          </a>
        ))}
      </div>
    </section>
  );
}

function StatsTab({ stats }) {
  const max = Math.max(1, ...stats.by_day.map((d) => d.count));
  const ch = stats.channels || {};
  return (
    <div className="space-y-10" data-testid="stats-tab">
      <QuickLinksCard />
      <LiveNow />
      <PushControls />
      <NotifSettingsCard />
      <ActivityFeed />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {[["Návštevy", stats.total_visits], ["Kliky", stats.clicks], ["Dopyty", stats.total_leads], ["Konverzia", `${stats.conversion_pct}%`], ["Opustenia", stats.exits], ["Vyhraté", stats.won]].map(([l, v]) => (
          <div key={l} className="p-5 border border-white/10 bg-ink-2">
            <div className="text-white/40 text-xs font-mono2 uppercase tracking-widest">{l}</div>
            <div className="font-display text-4xl text-gold mt-2">{v}</div>
          </div>
        ))}
      </div>

      {/* Visitor analytics */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="visitor-analytics">
        {[
          [Users, "Unikátni návštevníci", stats.unique_visitors ?? 0],
          [Sparkles, "Noví návštevníci", stats.new_visitors ?? 0],
          [Repeat, "Vracajúci sa", stats.returning_visitors ?? 0],
          [BarChart3, "Priem. scroll", `${stats.avg_scroll_depth ?? 0}%`],
          [MousePointerClick, "Miera opustenia", `${stats.bounce_pct ?? 0}%`],
          [MapPin, "Zobr. mapy", stats.map_views ?? 0],
        ].map(([Icon, l, v]) => (
          <div key={l} className="p-5 border border-white/10 bg-ink-2">
            <Icon className="w-5 h-5 text-gold mb-2" />
            <div className="font-display text-3xl text-white leading-none">{v}</div>
            <div className="text-white/40 text-[10px] font-mono2 uppercase tracking-widest mt-2">{l}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[[MessageCircle, "WhatsApp", ch.whatsapp || 0], [Mail, "E-mail", ch.email || 0], [Phone, "Telefón", ch.phone || 0], [Globe, "Zobrazenia referencií", stats.references_views ?? 0]].map(([Icon, l, v]) => (
          <div key={l} className="p-5 border border-white/10 bg-ink-2 flex items-center gap-4">
            <Icon className="w-7 h-7 text-gold" />
            <div><div className="text-white/40 text-xs uppercase tracking-widest font-mono2">{l}</div><div className="font-display text-3xl text-white">{v}</div></div>
          </div>
        ))}
      </div>

      <section className="border border-white/10 p-6">
        <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-6">Návštevy za posledných 14 dní</div>
        <div className="flex items-end gap-1 h-40">
          {stats.by_day.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center justify-end">
              <div className="w-full bg-gold/70 hover:bg-gold" style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count ? "4px" : "0" }} title={`${d.day}: ${d.count}`} />
              <span className="text-white/30 text-[9px] mt-1">{d.day.slice(8)}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <section className="border border-white/10 p-6" data-testid="stats-top-pages">
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-4">Najnavštevovanejšie stránky</div>
          {(stats.top_pages || []).length === 0 && <p className="text-white/40 text-sm">Žiadne dáta.</p>}
          {(stats.top_pages || []).map((s) => (<div key={s.path} className="flex justify-between gap-3 py-2 border-b border-white/5 text-sm"><span className="text-white/70 truncate">{s.path}</span><span className="font-mono2 text-gold shrink-0">{s.count}</span></div>))}
        </section>
        <section className="border border-white/10 p-6">
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-4">Zdroje odporúčaní</div>
          {(stats.top_sources || []).length === 0 && <p className="text-white/40 text-sm">Žiadne dáta.</p>}
          {(stats.top_sources || []).map((s) => (<div key={s.source} className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/70">{s.source}</span><span className="font-mono2 text-gold">{s.count}</span></div>))}
        </section>
        <section className="border border-white/10 p-6">
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-4">Lieviky (funnely)</div>
          {(stats.funnels || []).length === 0 && <p className="text-white/40 text-sm">Žiadne dáta.</p>}
          {(stats.funnels || []).map((s) => (<div key={s.funnel} className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/70">{s.funnel}</span><span className="font-mono2 text-gold">{s.count}</span></div>))}
        </section>
        <section className="border border-white/10 p-6">
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-4">Dopyty podľa stavu</div>
          {Object.entries(stats.leads_by_status || {}).map(([k, v]) => (<div key={k} className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/70">{{ new: "Nový", contacted: "Kontaktovaný", won: "Vyhraný", lost: "Zrušený" }[k] || k}</span><span className="font-mono2 text-gold">{v}</span></div>))}
        </section>
      </div>

      {/* Section engagement heatmap + scroll distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="section-heatmap">
        <section className="border border-white/10 p-6 lg:col-span-2">
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-6">Heatmapa sekcií — čo ľudí najviac zaujme</div>
          {(() => {
            const rows = stats.section_heatmap || [];
            if (rows.length === 0) return <p className="text-white/40 text-sm">Zatiaľ žiadne dáta o sekciách.</p>;
            const hmax = Math.max(1, ...rows.map((r) => r.count));
            return (
              <div className="space-y-3">
                {rows.map((r) => (
                  <div key={r.section} className="flex items-center gap-3">
                    <div className="w-40 shrink-0 text-white/70 text-sm truncate">{r.section}</div>
                    <div className="flex-1 bg-white/5 h-6 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-gold/70" style={{ width: `${(r.count / hmax) * 100}%` }} />
                    </div>
                    <div className="w-12 text-right font-mono2 text-gold text-sm">{r.count}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </section>
        <section className="border border-white/10 p-6">
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-6">Ako hlboko ľudia scrollujú</div>
          {(() => {
            const dist = stats.scroll_distribution || [];
            const smax = Math.max(1, ...dist.map((d) => d.count));
            if (dist.every((d) => d.count === 0)) return <p className="text-white/40 text-sm">Zatiaľ žiadne dáta o scrollovaní.</p>;
            return (
              <div className="space-y-3">
                {dist.map((d) => (
                  <div key={d.bucket} className="flex items-center gap-3">
                    <div className="w-20 shrink-0 text-white/70 text-sm">{d.bucket}</div>
                    <div className="flex-1 bg-white/5 h-5 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-gold/60" style={{ width: `${(d.count / smax) * 100}%` }} />
                    </div>
                    <div className="w-10 text-right font-mono2 text-gold text-sm">{d.count}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </section>
      </div>
    </div>
  );
}

function BlogTab({ blog, settings, onGenerate, onToggle, onDelete, onSaveSettings, onEdit }) {
  const [topic, setTopic] = useState("");
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  const totalViews = blog.reduce((a, p) => a + (p.views || 0), 0);
  return (
    <div className="space-y-8" data-testid="blog-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-white/10 bg-ink-2 p-5"><div className="font-display text-4xl">{blog.length}</div><div className="text-white/50 text-xs mt-1">Článkov spolu</div></div>
        <div className="border border-white/10 bg-ink-2 p-5"><div className="font-display text-4xl">{blog.filter((p) => p.published).length}</div><div className="text-white/50 text-xs mt-1">Publikovaných</div></div>
        <div className="border border-gold/30 bg-ink-2 p-5"><div className="font-display text-4xl text-gold" data-testid="blog-total-views">{totalViews}</div><div className="text-white/50 text-xs mt-1">Zobrazení spolu</div></div>
        <div className="border border-white/10 bg-ink-2 p-5"><div className="font-display text-4xl">{s.auto_blog ? s.per_day : 0}</div><div className="text-white/50 text-xs mt-1">Auto-článkov / deň</div></div>
      </div>

      <section className="border border-white/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-3">Vygenerovať článok teraz</div>
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Téma (nepovinné) — prázdne = auto téma" data-testid="blog-topic"
            className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm mb-3" />
          <button onClick={() => { onGenerate(topic); setTopic(""); }} data-testid="blog-generate-btn"
            className="flex items-center gap-2 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light"><Sparkles className="w-4 h-4" /> Generovať a publikovať</button>
        </div>
        <div>
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-3">Automatické publikovanie</div>
          <label className="flex items-center gap-3 text-white/70 text-sm mb-3">
            <input type="checkbox" checked={s.auto_blog} data-testid="autoblog-toggle" onChange={(e) => setS({ ...s, auto_blog: e.target.checked })} className="w-5 h-5 accent-gold" />
            Zapnúť auto-blog (AI generuje články denne)
          </label>
          <label className="block text-white/50 text-xs mb-1">Počet článkov denne (max 10)</label>
          <select value={s.per_day} data-testid="autoblog-perday" onChange={(e) => setS({ ...s, per_day: NUM(e.target.value) })}
            className="w-32 bg-ink border border-white/15 px-3 py-2 text-sm font-mono2 text-gold mb-3">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button onClick={() => onSaveSettings(s)} data-testid="autoblog-save" className="flex items-center gap-2 border border-gold text-gold px-5 py-2 text-sm hover:bg-gold hover:text-ink"><Save className="w-4 h-4" /> Uložiť</button>
        </div>
      </section>

      <div className="border border-white/10">
        {blog.length === 0 && <p className="p-10 text-center text-white/40">Zatiaľ žiadne články.</p>}
        {blog.map((p) => (
          <div key={p.id} className="flex items-center gap-4 p-4 border-b border-white/5">
            {p.cover_image ? <img src={imgUrl(p.cover_image)} alt="" className="w-16 h-12 object-cover" /> : <div className="w-16 h-12 bg-white/5" />}
            <div className="flex-1 min-w-0">
              <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`view-blog-${p.id}`}
                className="text-white font-medium truncate block hover:text-gold transition-colors" title={`Otvoriť článok: /blog/${p.slug}`}>{p.title}</a>
              <div className="text-white/40 text-xs">{p.category} · {new Date(p.created_at).toLocaleDateString("sk-SK")} · {p.source} · <span className="text-gold">{p.views || 0} zobrazení</span></div>
            </div>
            <button onClick={() => onEdit(p)} data-testid={`edit-blog-${p.id}`} className="p-2 border border-white/20 hover:border-gold" title="Upraviť"><Edit3 className="w-4 h-4" /></button>
            <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="Pozrieť článok na webe" className="p-2 border border-white/20 hover:border-gold text-gold"><ExternalLink className="w-4 h-4" /></a>
            <button onClick={() => onToggle(p)} className={`text-xs px-3 py-1 ${p.published ? "bg-gold/20 text-gold" : "bg-white/10 text-white/50"}`}>{p.published ? "Publikované" : "Skryté"}</button>
            <button onClick={() => onDelete(p.id)} className="p-2 border border-white/20 hover:border-red-500 text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlogEditor({ post, onClose, onSave }) {
  const [f, setF] = useState({
    id: post.id, title: post.title || "", category: post.category || "Zateplenie", excerpt: post.excerpt || "",
    content_html: post.content_html || "", cover_image: post.cover_image || "", published: post.published ?? true,
    meta_title: post.meta_title || "", meta_description: post.meta_description || "",
    focus_town: post.focus_town || "", focus_keyword: post.focus_keyword || "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div className="fixed inset-0 z-[200] bg-ink/90 backdrop-blur-md overflow-y-auto p-4 md:p-8" data-testid="blog-editor">
      <div className="max-w-3xl mx-auto bg-ink-2 border border-white/10 p-6 md:p-8">
        <div className="flex items-center justify-between mb-6"><h3 className="font-display text-3xl">Upraviť článok</h3><button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-6 h-6" /></button></div>
        <div className="space-y-4">
          <TextField label="Titulok" value={f.title} onChange={(v) => set("title", v)} testid="be-title" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Kategória" value={f.category} onChange={(v) => set("category", v)} testid="be-category" />
            <div><label className="block text-white/50 text-xs mb-2">Titulná fotka</label><ImageUpload value={f.cover_image} testid="be-cover" label="Nahrať fotku" onUploaded={(url) => set("cover_image", url)} /></div>
          </div>
          <div><label className="block text-white/50 text-xs mb-1">Krátky popis (excerpt)</label><textarea value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} data-testid="be-excerpt" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm resize-none focus:border-gold outline-none" /></div>
          <div><label className="block text-white/50 text-xs mb-1">Obsah článku (HTML)</label><textarea value={f.content_html} onChange={(e) => set("content_html", e.target.value)} rows={12} data-testid="be-content" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm font-mono2 resize-y focus:border-gold outline-none" /></div>

          <div className="border-t border-white/10 pt-4">
            <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-3">SEO a GEO nastavenia</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Meta titulok (SEO)" value={f.meta_title} onChange={(v) => set("meta_title", v)} testid="be-meta-title" />
              <TextField label="Zameranie — mesto/obec (GEO)" value={f.focus_town} onChange={(v) => set("focus_town", v)} testid="be-focus-town" />
              <TextField label="Zameranie — kľúčové slovo" value={f.focus_keyword} onChange={(v) => set("focus_keyword", v)} testid="be-focus-keyword" />
            </div>
            <div className="mt-4"><label className="block text-white/50 text-xs mb-1">Meta popis (SEO)</label><textarea value={f.meta_description} onChange={(e) => set("meta_description", e.target.value)} rows={2} data-testid="be-meta-desc" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm resize-none focus:border-gold outline-none" /></div>
          </div>

          <label className="flex items-center gap-3 text-white/70 text-sm"><input type="checkbox" checked={f.published} onChange={(e) => set("published", e.target.checked)} data-testid="be-published" className="w-5 h-5 accent-gold" /> Publikované</label>
        </div>
        <div className="flex gap-3 mt-6"><button onClick={() => onSave(f)} data-testid="be-save" className="flex-1 bg-gold text-ink py-3 font-bold uppercase tracking-wider hover:bg-gold-light">Uložiť článok</button><button onClick={onClose} className="px-6 py-3 border border-white/20 hover:border-white/50">Zrušiť</button></div>
      </div>
    </div>
  );
}

function ApplicationsTab({ apps, onStatus }) {
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid="applications-tab">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-white/10 text-white/50 font-mono2 text-xs uppercase tracking-widest"><th className="text-left p-4">Žiadateľ</th><th className="text-left p-4">Kontakt</th><th className="text-left p-4">Okres</th><th className="text-left p-4">Správa</th><th className="text-left p-4">Stav</th></tr></thead>
        <tbody>
          {apps.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-white/40">Žiadne žiadosti.</td></tr>}
          {apps.map((a) => (
            <tr key={a.id} className="border-b border-white/5 hover:bg-white/5">
              <td className="p-4"><div className="text-white font-medium">{a.name}</div><div className="text-white/40 text-xs">{a.company}</div></td>
              <td className="p-4 text-white/70"><div>{a.email}</div><div className="text-white/40 text-xs">{a.phone}</div></td>
              <td className="p-4 text-gold">{a.district || "—"}</td>
              <td className="p-4 text-white/60 text-xs max-w-xs">{a.message}</td>
              <td className="p-4"><select value={a.status} onChange={(e) => onStatus(a.id, e.target.value)} className="bg-ink border border-white/20 px-2 py-1 text-xs"><option value="new">Nová</option><option value="contacted">Kontaktovaný</option><option value="approved">Schválený</option><option value="rejected">Zamietnutý</option></select></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VatInputs({ value, vatPct, testid, onChange }) {
  const vat = vatPct || 23;
  const toVat = (v) => Math.round(v * (1 + vat / 100) * 100) / 100;
  const toNoVat = (v) => Math.round((v / (1 + vat / 100)) * 1000) / 1000;
  const inp = "bg-transparent border border-white/15 px-2 py-1.5 text-sm font-mono2 text-gold w-24";
  return (
    <div className="flex items-center gap-1.5">
      <input type="number" step="0.01" className={inp} value={value} data-testid={`${testid}-bez-dph`}
        onChange={(e) => onChange(NUM(e.target.value))} title="Cena bez DPH" />
      <span className="text-white/30 text-xs">bez DPH</span>
      <input type="number" step="0.01" className={inp} value={toVat(value)} data-testid={`${testid}-s-dph`}
        onChange={(e) => onChange(toNoVat(NUM(e.target.value)))} title="Cena s DPH" />
      <span className="text-white/30 text-xs">s DPH</span>
    </div>
  );
}

function ConsumableRow({ c, vatPct, onChange }) {
  const set = (k, v) => onChange({ ...c, [k]: v });
  const setVar = (i, k, v) => { const vs = [...(c.variants || [])]; vs[i] = { ...vs[i], [k]: v }; set("variants", vs); };
  const small = "bg-transparent border border-white/15 px-2 py-1.5 text-sm w-20";
  return (
    <div className="border border-white/10 p-4 space-y-3" data-testid={`cons-${c.key}`}>
      <div className="flex flex-wrap items-center gap-3">
        <input className="flex-1 min-w-[220px] bg-transparent border border-white/15 px-3 py-1.5 text-sm" value={c.name}
          onChange={(e) => set("name", e.target.value)} data-testid={`cons-name-${c.key}`} />
        {c.kind !== "plaster" && c.kind !== "anchors" && (
          <VatInputs value={c.price_no_vat ?? 0} vatPct={vatPct} testid={`cons-price-${c.key}`} onChange={(v) => set("price_no_vat", v)} />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm text-white/60">
        {(c.kind === "weight_pack" || c.kind === "area_pack") && (<>
          <label className="flex items-center gap-2">Spotreba/m² <input type="number" step="0.01" className={small} value={c.consumption_per_m2} data-testid={`cons-usage-${c.key}`} onChange={(e) => set("consumption_per_m2", NUM(e.target.value))} /></label>
          <label className="flex items-center gap-2">Balenie <input type="number" step="1" className={small} value={c.pack_size} data-testid={`cons-pack-${c.key}`} onChange={(e) => set("pack_size", NUM(e.target.value))} /></label>
        </>)}
        {c.kind === "units_per_area" && (
          <label className="flex items-center gap-2">1 ks na m² <input type="number" step="1" className={small} value={c.m2_per_unit} data-testid={`cons-usage-${c.key}`} onChange={(e) => set("m2_per_unit", NUM(e.target.value))} /></label>
        )}
        {c.kind === "caps" && (
          <label className="flex items-center gap-2">Ks v balení <input type="number" step="1" className={small} value={c.pack_size} data-testid={`cons-pack-${c.key}`} onChange={(e) => set("pack_size", NUM(e.target.value))} /> <span className="text-white/30">(počet kusov = počet kotiev)</span></label>
        )}
        {c.kind === "anchors" && (<>
          <label className="flex items-center gap-2">Ks/m² <input type="number" step="0.5" className={small} value={c.per_m2} data-testid={`cons-usage-${c.key}`} onChange={(e) => set("per_m2", NUM(e.target.value))} /></label>
          <label className="flex items-center gap-2">Nádstavba mm <input type="number" step="5" className={small} value={c.extra_mm} onChange={(e) => set("extra_mm", NUM(e.target.value))} /></label>
        </>)}
      </div>
      {(c.kind === "anchors" || c.kind === "plaster") && (
        <div className="space-y-2">
          <div className="text-white/40 text-xs uppercase tracking-wider">{c.kind === "anchors" ? "Varianty dĺžok (vyberie sa najbližšia ≥ hrúbka + nádstavba)" : "Typy omietky a ich výdatnosť"}</div>
          {(c.variants || []).map((v, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              {c.kind === "anchors" ? (
                <label className="flex items-center gap-2 text-sm text-white/60">Dĺžka mm <input type="number" className={small} value={v.length_mm} data-testid={`cons-varlen-${c.key}-${i}`} onChange={(e) => setVar(i, "length_mm", NUM(e.target.value))} /></label>
              ) : (<>
                <input className="bg-transparent border border-white/15 px-2 py-1.5 text-sm flex-1 min-w-[180px]" value={v.name} onChange={(e) => setVar(i, "name", e.target.value)} />
                <label className="flex items-center gap-2 text-sm text-white/60">Výdatnosť m²/bal <input type="number" className={small} value={v.coverage_m2} onChange={(e) => setVar(i, "coverage_m2", NUM(e.target.value))} /></label>
              </>)}
              <VatInputs value={v.price_no_vat ?? 0} vatPct={vatPct} testid={`cons-var-${c.key}-${i}`} onChange={(val) => setVar(i, "price_no_vat", val)} />
              <button onClick={() => set("variants", c.variants.filter((_, j) => j !== i))} data-testid={`cons-del-var-${c.key}-${i}`} className="p-1.5 border border-white/20 text-red-400 hover:border-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={() => set("variants", [...(c.variants || []), c.kind === "anchors" ? { length_mm: 315, price_no_vat: 0.22 } : { key: `typ${(c.variants || []).length + 1}`, name: "Nový typ", coverage_m2: 12, price_no_vat: 50 }])}
            data-testid={`cons-add-var-${c.key}`} className="flex items-center gap-1 text-gold text-sm"><Plus className="w-4 h-4" /> Pridať variant</button>
        </div>
      )}
    </div>
  );
}

function PricingTab({ pricing, setPricing, save, saving }) {
  return (
    <div className="space-y-10">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-4xl">Cenník materiálov a prác</h2>
        <button onClick={save} disabled={saving} data-testid="admin-save-pricing" className="flex items-center gap-2 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light"><Save className="w-4 h-4" /> {saving ? "Ukladám…" : "Uložiť cenník"}</button>
      </div>
      <section className="border border-white/10">
        <div className="px-5 py-3 border-b border-white/10 font-mono2 text-xs uppercase tracking-widest text-gold">Izolácia — cena € / m³ (cena za m² = m³ cena × hrúbka / 1000)</div>
        <div className="p-5 space-y-3">
          {pricing.materials.map((m, i) => (
            <div key={m.key} className="grid grid-cols-12 gap-3 items-center">
              <input className="col-span-6 bg-transparent border border-white/15 px-3 py-2 text-sm" value={m.name} data-testid={`mat-name-${m.key}`} onChange={(e) => { const mm = [...pricing.materials]; mm[i] = { ...m, name: e.target.value }; setPricing({ ...pricing, materials: mm }); }} />
              <input type="number" step="0.5" className="col-span-3 bg-transparent border border-white/15 px-3 py-2 text-sm font-mono2 text-gold" value={m.price_m3 ?? (m.price_per_mm_m2 * 1000)} data-testid={`mat-price-${m.key}`}
                onChange={(e) => { const v = NUM(e.target.value); const mm = [...pricing.materials]; mm[i] = { ...m, price_m3: v, price_per_mm_m2: Math.round(v / 1000 * 1000) / 1000 }; setPricing({ ...pricing, materials: mm }); }} />
              <span className="col-span-3 text-white/40 text-xs font-mono2">€/m³ · {((m.price_m3 ?? m.price_per_mm_m2 * 1000) / 1000).toFixed(3)} €/mm/m²</span>
            </div>
          ))}
        </div>
      </section>
      <section className="border border-white/10">
        <div className="px-5 py-3 border-b border-white/10 font-mono2 text-xs uppercase tracking-widest text-gold">Spotrebný materiál — automatický výpočet zo spotreby (balenia sa zaokrúhľujú hore)</div>
        <div className="p-5 space-y-4">
          {(pricing.consumables || []).map((c, i) => (
            <ConsumableRow key={c.key} c={c} vatPct={pricing.vat_pct}
              onChange={(nc) => { const arr = [...pricing.consumables]; arr[i] = nc; setPricing({ ...pricing, consumables: arr }); }} />
          ))}
        </div>
      </section>
      <section className="border border-white/10">
        <div className="px-5 py-3 border-b border-white/10 font-mono2 text-xs uppercase tracking-widest text-gold">Práca, lešenie, doprava</div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
          {[["labor_per_m2", "Práca (bez partnera)"], ["scaffolding_per_m2", "Lešenie"], ["transport_per_m2", "Doprava / m²"], ["transport_base", "Doprava paušál (€)"], ["urgency_surcharge_pct", "Expres príplatok (%)"], ["vat_pct", "DPH (%)"]].map(([k, l]) => (
            <NumField key={k} label={l} value={pricing[k]} testid={`rate-${k}`} onChange={(v) => setPricing({ ...pricing, [k]: v })} />
          ))}
        </div>
        <div className="px-5 pb-4 text-white/30 text-xs">Materiálové zložky (lepidlo, kotvy, sieťka, penetrácia, omietka) sa počítajú automaticky zo spotreby v sekcii vyššie.</div>
      </section>
      <section className="border border-white/10">
        <div className="px-5 py-3 border-b border-white/10 font-mono2 text-xs uppercase tracking-widest text-gold">Presný režim — výkaz výmery (lišty, parapety, lešenie, sokel, odpad)</div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
          {[["floor_height_m", "Výška podlažia (m)", "0.1"], ["scaffold_corner_extra_m", "Lešenie: rezerva/nárožie (m)", "0.1"],
            ["scaffold_setup_per_m2", "Lešenie: montáž (€/m²)", "0.1"], ["scaffold_rent_per_m2", "Lešenie: prenájom (€/m²)", "0.1"],
            ["scaffold_dismantle_per_m2", "Lešenie: demontáž (€/m²)", "0.1"], ["starter_profile_per_m", "Štartovacia lišta (€/bm)", "0.1"],
            ["sill_per_m", "Parapet (€/bm)", "0.5"], ["sill_extra_mm", "Parapet: presah nad izoláciu (mm)", "5"],
            ["sokel_xps_per_m2", "Sokel XPS systém (€/m²)", "0.5"], ["sokel_profile_per_m", "Soklová lišta (€/bm)", "0.1"],
            ["sokel_default_height_m", "Predvolená výška soklu (m)", "0.1"],
            ["waste_pct", "Odpad / rezanie (% materiálu)", "0.5"], ["profile_reserve_pct", "Rezerva na lišty (%)", "1"],
            ["corner_bead_per_m", "Rohová lišta so sieťkou (€/bm)", "0.1"], ["apu_per_m", "APU okenná lišta (€/bm)", "0.1"],
            ["drip_per_m", "Okapová lišta (€/bm)", "0.1"], ["reveal_per_m", "Špaleta — zateplenie ostenia (€/bm)", "0.5"],
            ["soffit_fascia_m", "Podhľad: čielko (m)", "0.05"],
            ["soffit_battens_per_m2", "Podhľad: laty po 25 cm (€/m²)", "1"], ["soffit_osb_per_m2", "Podhľad: OSB debnenie (€/m²)", "1"],
            ["soffit_koniky_per_m2", "Podhľad: koníky (€/m²)", "1"]].map(([k, l, st]) => (
            <NumField key={k} label={l} step={st} value={(pricing.precise || {})[k] ?? ""} testid={`precise-${k}`}
              onChange={(v) => setPricing({ ...pricing, precise: { ...(pricing.precise || {}), [k]: v } })} />
          ))}
        </div>
        <div className="px-5 pb-4 text-white/30 text-xs">
          Presný režim používateľ spustí v kalkulačke zadaním pôdorysu, podlaží a okien — alebo ho predvyplní AI z nahraných dokumentov.
          Parapety: hĺbka = hrúbka izolácie + presah; okná od zeme a dvere majú 0 bm. Špalety, rohová a APU lišta z obvodov otvorov.
          Okapová lišta = zakladanie + horné hrany otvorov + obvod podhľadu. Podhľad má 3 varianty montáže, do sumy ide zvolená.
          Lešenie sa v presnom režime počíta z rozmerov domu namiesto paušálnej sadzby za m² fasády.
        </div>
      </section>
      <section className="border border-white/10">
        <div className="px-5 py-3 border-b border-white/10 font-mono2 text-xs uppercase tracking-widest text-gold">Marža platformy na materiáli (% + fixná €/m²)</div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[["insulation_pct", "Izolácia / dosky (%)", "0.5"], ["adhesive_pct", "Lepidlá a stierky (%)", "0.5"],
            ["mesh_pct", "Sieťky (%)", "0.5"], ["anchors_pct", "Kotvy a EPS zátky (%)", "0.5"],
            ["plaster_pct", "Omietky (%)", "0.5"], ["other_pct", "Ostatné — penetrák, pásky, PU pena (%)", "0.5"],
            ["fixed_per_m2", "Fixná marža (€/m² plochy)", "0.1"]].map(([k, l, st]) => (
            <NumField key={k} label={l} step={st} value={(pricing.material_margin || {})[k] ?? ""} testid={`margin-${k}`}
              onChange={(v) => setPricing({ ...pricing, material_margin: { ...(pricing.material_margin || {}), [k]: v } })} />
          ))}
        </div>
        <div className="px-5 pb-4 text-white/30 text-xs">
          Marža sa pripočíta k nákupným cenám materiálu v kalkulačke. Prácu a lešenie nacenia partneri — províziu platformy nastavujete per partner (interne, zákazník ju nevidí).
        </div>
      </section>
      <section className="border border-white/10">
        <div className="px-5 py-3 border-b border-white/10 font-mono2 text-xs uppercase tracking-widest text-gold">Koeficient práce podľa objektu</div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(pricing.building_factors).map(([k, v]) => (<NumField key={k} step="0.01" label={{ family_house: "Rodinný dom", apartment_building: "Bytový dom", building: "Budova" }[k] || k} value={v} testid={`factor-${k}`} onChange={(nv) => setPricing({ ...pricing, building_factors: { ...pricing.building_factors, [k]: nv } })} />))}
        </div>
      </section>
    </div>
  );
}

function PartnersTab({ partners, onAdd, onEdit, onDelete, onToggleActive, onToggleFeatured }) {
  const pending = partners.filter((p) => !p.active).length;
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-4xl">Partneri / realizátori</h2>
          {pending > 0 && <p className="text-gold text-sm mt-1" data-testid="pending-partners-note">{pending} nových čaká na schválenie</p>}
        </div>
        <button onClick={onAdd} data-testid="add-partner-btn" className="flex items-center gap-2 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light"><Plus className="w-4 h-4" /> Pridať partnera</button>
      </div>
      {partners.length === 0 && <p className="text-white/40 border border-white/10 p-10 text-center">Zatiaľ žiadni partneri.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="partners-list">
        {partners.map((p) => (
          <div key={p.id} className={`border bg-ink-2 p-6 ${p.active ? "border-white/10" : "border-gold/40"}`}>
            <div className="flex items-start justify-between">
              <div><div className="font-display text-2xl text-white">{p.company || p.name}</div><div className="text-white/50 text-sm">{p.name} · {p.email || "—"}</div></div>
              <span className={`text-xs px-2 py-1 ${p.active ? "bg-gold/20 text-gold" : "bg-white/10 text-white/60"}`}>{p.active ? "Aktívny" : "Čaká na schválenie"}</span>
            </div>
            <div className="flex flex-wrap gap-1 mt-3">{p.districts.slice(0, 6).map((d) => <span key={d} className="text-xs border border-white/15 px-2 py-0.5 text-white/60">{d}</span>)}{p.districts.length > 6 && <span className="text-xs text-white/40">+{p.districts.length - 6}</span>}</div>
            <div className="flex items-center gap-4 mt-4 font-mono2 text-sm"><span className="text-gold">{p.labor_per_m2} €/m²</span><span className="text-white/50">provízia {p.commission_pct}%</span>{p.rating ? <span className="text-white/50">★ {p.rating}</span> : null}<span className="text-white/40">{(p.references || []).length} ref.</span></div>
            <div className="flex flex-wrap gap-2 mt-5">
              <button onClick={() => onToggleActive(p)} data-testid={`toggle-partner-${p.id}`} className={`flex items-center gap-2 px-4 py-2 text-sm font-bold ${p.active ? "border border-white/20 text-white/70 hover:border-white/50" : "bg-gold text-ink hover:bg-gold-light"}`}>{p.active ? "Deaktivovať" : "Schváliť a aktivovať"}</button>
              <button onClick={() => onToggleFeatured(p)} data-testid={`featured-partner-${p.id}`} title="Top partner — zobrazí sa prvý v ponukách"
                className={`flex items-center gap-2 px-4 py-2 text-sm font-bold ${p.featured ? "bg-gold text-ink" : "border border-white/20 text-white/60 hover:border-gold"}`}>
                <Star className={`w-4 h-4 ${p.featured ? "fill-ink" : ""}`} /> {p.featured ? "Top partner" : "Nastaviť top"}
              </button>
              <button onClick={() => onEdit(p)} data-testid={`edit-partner-${p.id}`} className="flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-gold text-sm"><Edit3 className="w-4 h-4" /> Upraviť</button>
              <button onClick={() => onDelete(p.id)} className="flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-red-500 text-sm text-red-400"><Trash2 className="w-4 h-4" /> Zmazať</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnerEditor({ partner, allDistricts, onClose, onSave }) {
  const [f, setF] = useState({
    id: partner.id, name: partner.name || "", company: partner.company || "", email: partner.email || "",
    phone: partner.phone || "", districts: partner.districts || [], labor_per_m2: partner.labor_per_m2 ?? 20,
    commission_pct: partner.commission_pct ?? 15, active: partner.active ?? true, bio: partner.bio || "",
    featured: partner.featured ?? false, labor_matrix: partner.labor_matrix || {}, scaffold_rates: partner.scaffold_rates || {},
    rating: partner.rating ?? "", reviews_count: partner.reviews_count ?? 0, google_url: partner.google_url || "",
    references: partner.references || [], logo: partner.logo || "", login_password: "",
  });
  const [search, setSearch] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleDistrict = (d) => setF((x) => ({ ...x, districts: x.districts.includes(d) ? x.districts.filter((y) => y !== d) : [...x.districts, d] }));
  const filtered = allDistricts.filter((d) => d.toLowerCase().includes(search.toLowerCase()));
  const addRef = () => set("references", [...f.references, { location: "", description: "", images: [] }]);
  const updRef = (i, k, v) => set("references", f.references.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const delRef = (i) => set("references", f.references.filter((_, idx) => idx !== i));
  const submit = () => { if (!f.name) return toast.error("Zadajte meno"); onSave({ ...f, labor_per_m2: NUM(f.labor_per_m2), commission_pct: NUM(f.commission_pct), rating: f.rating === "" ? null : NUM(f.rating), reviews_count: NUM(f.reviews_count) }); };

  return (
    <div className="fixed inset-0 z-[200] bg-ink/90 backdrop-blur-md overflow-y-auto p-4 md:p-8" data-testid="partner-editor">
      <div className="max-w-3xl mx-auto bg-ink-2 border border-white/10 p-6 md:p-8">
        <div className="flex items-center justify-between mb-6"><h3 className="font-display text-3xl">{f.id ? "Upraviť partnera" : "Nový partner"}</h3><button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-6 h-6" /></button></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <TextField label="Meno *" value={f.name} onChange={(v) => set("name", v)} testid="pe-name" />
          <TextField label="Firma" value={f.company} onChange={(v) => set("company", v)} testid="pe-company" />
          <TextField label="E-mail (prihlasovací)" value={f.email} onChange={(v) => set("email", v)} testid="pe-email" />
          <TextField label="Telefón" value={f.phone} onChange={(v) => set("phone", v)} testid="pe-phone" />
          <TextField label="Prihlasovacie heslo (nastaviť/zmeniť)" value={f.login_password} onChange={(v) => set("login_password", v)} testid="pe-password" />
          <NumField label="Cena práce (€/m²)" value={f.labor_per_m2} onChange={(v) => set("labor_per_m2", v)} testid="pe-labor" />
          <NumField label="Naša provízia (%)" value={f.commission_pct} onChange={(v) => set("commission_pct", v)} testid="pe-commission" />
          <NumField label="Google hodnotenie (★)" step="0.1" value={f.rating} onChange={(v) => set("rating", v)} testid="pe-rating" />
          <NumField label="Počet recenzií" value={f.reviews_count} onChange={(v) => set("reviews_count", v)} testid="pe-reviews" />
          <TextField label="Google odkaz" value={f.google_url} onChange={(v) => set("google_url", v)} testid="pe-google" />
          <div><label className="block text-white/50 text-xs mb-2">Logo firmy</label><ImageUpload value={f.logo || ""} testid="pe-logo" label="Nahrať logo" onUploaded={(url) => set("logo", url)} /></div>
          <label className="flex items-center gap-3 mt-6"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="pe-active" className="w-5 h-5 accent-gold" /><span className="text-white/70 text-sm">Aktívny</span></label>
          <label className="flex items-center gap-3 mt-6"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="pe-featured" className="w-5 h-5 accent-gold" /><span className="text-gold text-sm">Top partner (zobrazí sa prvý v ponukách)</span></label>
        </div>
        <div className="mb-4"><label className="block text-white/50 text-xs mb-1">Popis / bio</label><textarea value={f.bio} onChange={(e) => set("bio", e.target.value)} rows={2} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm resize-none" /></div>
        <div className="mb-6">
          <label className="block text-white/50 text-xs mb-2">Okresy pôsobenia ({f.districts.length})</label>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hľadať okres…" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm mb-2" data-testid="pe-district-search" />
          <div className="max-h-48 overflow-y-auto border border-white/10 p-3 grid grid-cols-2 sm:grid-cols-3 gap-1">
            {filtered.map((d) => (<label key={d} className={`flex items-center gap-2 text-xs px-2 py-1 cursor-pointer ${f.districts.includes(d) ? "text-gold" : "text-white/60"}`}><input type="checkbox" checked={f.districts.includes(d)} onChange={() => toggleDistrict(d)} className="accent-gold" />{d}</label>))}
          </div>
        </div>
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2"><label className="text-white/50 text-xs">Realizácie (lokalita, popis, foto — zobrazia sa na mape)</label><button onClick={addRef} data-testid="pe-add-ref" className="flex items-center gap-1 text-gold text-xs"><Plus className="w-3 h-3" /> Pridať</button></div>
          <div className="space-y-3">
            {f.references.map((r, i) => (
              <div key={i} className="border border-white/10 p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                <input className="md:col-span-3 bg-transparent border border-white/15 px-2 py-1.5 text-xs" placeholder="Lokalita" value={r.location} onChange={(e) => updRef(i, "location", e.target.value)} />
                <input className="md:col-span-4 bg-transparent border border-white/15 px-2 py-1.5 text-xs" placeholder="Popis" value={r.description} onChange={(e) => updRef(i, "description", e.target.value)} />
                <div className="md:col-span-4"><PhotoGalleryEditor images={r.images || (r.image ? [r.image] : [])} testid={`pe-ref-photos-${i}`} label="Fotky (pred/po)" onChange={(imgs) => updRef(i, "images", imgs)} /></div>
                <button onClick={() => delRef(i)} className="md:col-span-1 text-red-400 flex justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-3"><button onClick={submit} data-testid="pe-save" className="flex-1 bg-gold text-ink py-3 font-bold uppercase tracking-wider hover:bg-gold-light">Uložiť partnera</button><button onClick={onClose} className="px-6 py-3 border border-white/20 hover:border-white/50">Zrušiť</button></div>
      </div>
    </div>
  );
}

function IndexNowButton() {
  const [busy, setBusy] = useState(false);
  const ping = async () => {
    setBusy(true);
    try {
      const { data } = await authApi().post("/admin/indexnow");
      if (data.ok) toast.success(`IndexNow: odoslaných ${data.count} URL (HTTP ${data.status})`);
      else toast.error(`IndexNow odpovedal HTTP ${data.status}`);
    } catch { toast.error("IndexNow sa nepodarilo kontaktovať"); } finally { setBusy(false); }
  };
  return (
    <button onClick={ping} disabled={busy} data-testid="indexnow-ping-btn"
      className="flex items-center gap-2 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light disabled:opacity-50">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Odoslať top URL
    </button>
  );
}

function MapConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    authApi().get("/admin/map-config").then((r) => setCfg(r.data)).catch(() => setCfg({ provider: "osm", key: "" }));
  }, []);
  if (!cfg) return null;
  const save = async () => {
    setSaving(true);
    try {
      await authApi().post("/admin/map-config", { provider: cfg.provider, key: cfg.key || "" });
      resetTileConfig();
      toast.success("Podklad mapy uložený — prejaví sa po obnovení stránky");
    } catch { toast.error("Nepodarilo sa uložiť"); } finally { setSaving(false); }
  };
  return (
    <section className="border border-white/10 p-5" data-testid="map-config">
      <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-2 flex items-center gap-2"><MapIcon className="w-4 h-4" /> Podklad mapy</div>
      <p className="text-white/50 text-sm mb-4">
        Tmavý podklad zladený s dizajnom webu. Kľúč získate zdarma: CARTO (app.carto.com → Developers → API Keys) alebo Stadia Maps (stadiamaps.com → API key). Bez kľúča sa použije bezplatný OpenStreetMap.
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <select value={cfg.provider} data-testid="map-provider" onChange={(e) => setCfg({ ...cfg, provider: e.target.value })}
          className="bg-ink border border-white/20 px-3 py-2 text-sm text-white">
          <option value="osm">OpenStreetMap (bez kľúča)</option>
          <option value="carto_dark">CARTO Dark (tmavá)</option>
          <option value="stadia_dark">Stadia Alidade Dark (tmavá)</option>
        </select>
        {cfg.provider !== "osm" && (
          <input value={cfg.key || ""} onChange={(e) => setCfg({ ...cfg, key: e.target.value })} placeholder="API kľúč…" data-testid="map-api-key"
            className="bg-transparent border border-white/20 px-3 py-2 text-sm flex-1 min-w-[240px] focus:border-gold outline-none" />
        )}
        <button onClick={save} disabled={saving} data-testid="map-config-save"
          className="flex items-center gap-2 bg-gold text-ink px-6 py-2.5 font-bold uppercase tracking-wider text-sm hover:bg-gold-light disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložiť
        </button>
      </div>
    </section>
  );
}

function SeoTab({ summary, keywords, onSave, onDelete, onImport }) {
  const [kwEdit, setKwEdit] = useState(null);
  const [csv, setCsv] = useState("");
  const [kwQuery, setKwQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const q = kwQuery.trim().toLowerCase();
  const filtered = keywords.filter((k) => q
    ? (k.slug.includes(q) || (k.h1_template || "").toLowerCase().includes(q))
    : (showInactive || k.active));
  const shownKw = filtered.slice(0, 200);
  return (
    <div className="space-y-10" data-testid="seo-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Obce", summary?.towns], ["Kľúčové slová", summary?.keywords_active], ["Vygenerované stránky", summary?.generated_pages], ["Sitemap", "XML"]].map(([l, v], i) => (
          <div key={l} className="p-6 border border-white/10 bg-ink-2">
            <div className="text-white/40 text-xs font-mono2 uppercase tracking-widest">{l}</div>
            {i === 3 ? <a href={`${backend}/api/sitemap.xml`} target="_blank" rel="noreferrer" className="font-display text-3xl text-gold mt-2 flex items-center gap-2" data-testid="sitemap-link">XML <ExternalLink className="w-5 h-5" /></a> : <div className="font-display text-5xl text-gold mt-2">{v ?? "…"}</div>}
          </div>
        ))}
      </div>
      <section className="border border-white/10">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between"><span className="font-mono2 text-xs uppercase tracking-widest text-gold">Kľúčové slová ({keywords.length})</span><button onClick={() => setKwEdit({})} data-testid="add-keyword-btn" className="flex items-center gap-1 text-gold text-sm"><Plus className="w-4 h-4" /> Pridať</button></div>
        <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-3">
          <input value={kwQuery} onChange={(e) => setKwQuery(e.target.value)} placeholder="Hľadať frázu…" data-testid="keyword-search"
            className="bg-transparent border border-white/15 px-3 py-2 text-sm flex-1 min-w-[200px] focus:border-gold outline-none" />
          <label className="text-white/60 text-sm flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="keyword-show-inactive" className="accent-gold" />
            Zobraziť aj neaktívne ({keywords.filter((k) => !k.active).length})
          </label>
        </div>
        <div className="p-5 space-y-3">
          {shownKw.map((k) => (
            <div key={k.id} className="flex items-center justify-between border border-white/10 p-4">
              <div><div className="text-white font-medium">{k.h1_template} <span className="text-white/30 font-mono2 text-xs">/{k.slug}/…</span></div><div className="text-white/40 text-xs mt-1">{k.title_template}</div></div>
              <div className="flex items-center gap-2"><span className={`text-xs px-2 py-1 ${k.active ? "bg-gold/20 text-gold" : "bg-white/10 text-white/50"}`}>{k.active ? "Aktívne" : "Vypnuté"}</span><button onClick={() => setKwEdit(k)} data-testid={`edit-keyword-${k.slug}`} className="p-2 border border-white/20 hover:border-gold"><Edit3 className="w-4 h-4" /></button><button onClick={() => onDelete(k.id)} className="p-2 border border-white/20 hover:border-red-500 text-red-400"><Trash2 className="w-4 h-4" /></button></div>
            </div>
          ))}
          {filtered.length > shownKw.length && (
            <div className="text-white/40 text-sm text-center pt-2" data-testid="keyword-more-note">…a ďalších {filtered.length - shownKw.length} fráz (spresnite vyhľadávanie)</div>
          )}
          {shownKw.length === 0 && <div className="text-white/40 text-sm" data-testid="keyword-empty">Žiadne frázy nezodpovedajú filtru.</div>}
        </div>
      </section>
      <section className="border border-white/10 p-5">
        <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-2">IndexNow — okamžitá indexácia (Bing, Yandex, Seznam)</div>
        <p className="text-white/50 text-sm mb-3">Odošle top URL (statické stránky, blog, top frázy × top mestá) vyhľadávačom na okamžitý crawl. Spúšťajte po každom deployi.</p>
        <IndexNowButton />
      </section>
      <MapConfigCard />
      <section className="border border-white/10 p-5">
        <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-3">Hromadný import obcí (CSV: Názov, Okres, Kraj)</div>
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6} data-testid="town-import-textarea" placeholder={"Lehnice, Dunajská Streda, Trnavský kraj"} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm font-mono2 resize-none" />
        <button onClick={() => { onImport(csv); setCsv(""); }} data-testid="import-towns-btn" className="mt-3 flex items-center gap-2 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light"><Save className="w-4 h-4" /> Importovať obce</button>
      </section>
      {kwEdit !== null && <KeywordEditor kw={kwEdit} onClose={() => setKwEdit(null)} onSave={(k) => { onSave(k); setKwEdit(null); }} />}
    </div>
  );
}

function KeywordEditor({ kw, onClose, onSave }) {
  const [f, setF] = useState({ id: kw.id, slug: kw.slug || "", h1_template: kw.h1_template || "Zateplenie {town}", title_template: kw.title_template || "Zateplenie {town} | Zateplenia.eu", intro_template: kw.intro_template || "Zateplenie domu {town} ({region}) — cena online do 60 sekúnd.", active: kw.active ?? true });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div className="fixed inset-0 z-[200] bg-ink/90 backdrop-blur-md overflow-y-auto p-4 md:p-8" data-testid="keyword-editor">
      <div className="max-w-2xl mx-auto bg-ink-2 border border-white/10 p-6 md:p-8">
        <div className="flex items-center justify-between mb-6"><h3 className="font-display text-3xl">{f.id ? "Upraviť" : "Nové"} kľúčové slovo</h3><button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-6 h-6" /></button></div>
        <p className="text-white/50 text-sm mb-4">Premenné: <span className="text-gold font-mono2">{"{town}"}</span>, <span className="text-gold font-mono2">{"{region}"}</span>, <span className="text-gold font-mono2">{"{okres}"}</span>.</p>
        <div className="space-y-4">
          <TextField label="Slug (prázdne = auto)" value={f.slug} onChange={(v) => set("slug", v)} testid="ke-slug" />
          <TextField label="H1 nadpis" value={f.h1_template} onChange={(v) => set("h1_template", v)} testid="ke-h1" />
          <TextField label="Title (SEO)" value={f.title_template} onChange={(v) => set("title_template", v)} testid="ke-title" />
          <div><label className="block text-white/50 text-xs mb-1">Úvodný text</label><textarea value={f.intro_template} onChange={(e) => set("intro_template", e.target.value)} rows={3} data-testid="ke-intro" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm resize-none" /></div>
          <label className="flex items-center gap-3 text-white/70 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="w-5 h-5 accent-gold" data-testid="ke-active" /> Aktívne</label>
        </div>
        <div className="flex gap-3 mt-6"><button onClick={() => onSave(f)} data-testid="ke-save" className="flex-1 bg-gold text-ink py-3 font-bold uppercase tracking-wider hover:bg-gold-light">Uložiť</button><button onClick={onClose} className="px-6 py-3 border border-white/20 hover:border-white/50">Zrušiť</button></div>
      </div>
    </div>
  );
}

function LeadsTab({ leads, setLeadStatus, onLeadUpdate }) {
  const [openId, setOpenId] = useState(null);
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="w-full text-sm" data-testid="leads-table">
        <thead><tr className="border-b border-white/10 text-white/50 font-mono2 text-xs uppercase tracking-widest"><th className="text-left p-4">Klient</th><th className="text-left p-4">Kontakt</th><th className="text-left p-4">Objekt</th><th className="text-right p-4">Cena</th><th className="text-left p-4">Stav</th><th className="p-4"></th></tr></thead>
        <tbody>
          {leads.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-white/40">Zatiaľ žiadne dopyty.</td></tr>}
          {leads.map((l) => (
            <FragmentRow key={l.id} lead={l} open={openId === l.id} onToggle={() => setOpenId(openId === l.id ? null : l.id)} setLeadStatus={setLeadStatus} onLeadUpdate={onLeadUpdate} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FragmentRow({ lead: l, open, onToggle, setLeadStatus, onLeadUpdate }) {
  const hasDetail = l.type !== "newsletter" && (l.calc_input?.precise || (l.plan_files || []).length > 0);
  return (
    <>
      <tr className="border-b border-white/5 hover:bg-white/5">
        <td className="p-4"><div className="text-white font-medium">{l.name || (l.source === "calc_email" ? "— (len e-mail)" : "—")}</div><div className="text-white/40 text-xs">{new Date(l.created_at).toLocaleString("sk-SK")}</div></td>
        <td className="p-4 text-white/70"><div>{l.email}</div><div className="text-white/40 text-xs">{l.phone} {l.city && `· ${l.city}`}</div></td>
        <td className="p-4 text-white/70 text-xs">{l.type === "newsletter" ? <span className="text-gold">Odber noviniek</span> : <>{l.result?.area} m² · {l.calc_input?.thickness} mm<br />{l.result?.material_name}{l.calc_input?.district ? ` · ${l.calc_input.district}` : ""}{l.calc_input?.precise ? <span className="block text-gold/80 mt-0.5">výkaz výmery{l.calc_input?.ai_used ? " · AI z pôdorysu" : ""}</span> : null}{l.calc_input?.ai_used && !l.calc_input?.precise ? <span className="block text-gold/80 mt-0.5">AI z pôdorysu</span> : null}{l.source === "calc_email" ? <span className="block text-gold mt-0.5">zachytený e-mail z kalkulačky</span> : null}</>}</td>
        <td className="p-4 text-right font-mono2 text-gold">{l.type === "newsletter" ? "—" : fmt(l.result?.total)}</td>
        <td className="p-4"><select value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className="bg-ink border border-white/20 px-2 py-1 text-xs"><option value="new">Nový</option><option value="contacted">Kontaktovaný</option><option value="won">Vyhraný</option><option value="lost">Zrušený</option></select></td>
        <td className="p-4">{hasDetail && <button onClick={onToggle} data-testid={`lead-detail-${l.id}`} className="text-white/40 hover:text-gold"><ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180 text-gold" : ""}`} /></button>}</td>
      </tr>
      {open && hasDetail && (
        <tr className="border-b border-white/10 bg-ink-2/50">
          <td colSpan={6} className="p-5"><LeadPreciseDetail lead={l} onLeadUpdate={onLeadUpdate} /></td>
        </tr>
      )}
    </>
  );
}

function LeadPreciseDetail({ lead, onLeadUpdate }) {
  const init = lead.calc_input?.precise || {};
  const [f, setF] = useState({
    length_m: init.length_m ?? "", width_m: init.width_m ?? "", floors: init.floors ?? 1,
    sokel_height_m: init.sokel_height_m ?? 0, soffit_width_m: init.soffit_width_m ?? 0,
    soffit_variant: init.soffit_variant || "battens",
    windows: (init.windows || []).map((w) => ({ width_m: w.width_m ?? "", height_m: w.height_m ?? "", count: w.count ?? 1, kind: w.kind || "window", full_height: !!w.full_height })),
  });
  const [saving, setSaving] = useState(false);
  const setW = (i, k, v) => setF((x) => ({ ...x, windows: x.windows.map((w, idx) => (idx === i ? { ...w, [k]: v } : w)) }));

  const download = async (file) => {
    try {
      const r = await authApi().get(`/admin/files/${file.path}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = file.name || file.path.split("/").pop(); a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Súbor sa nepodarilo stiahnuť"); }
  };

  const recalc = async () => {
    setSaving(true);
    try {
      const precise = {
        length_m: NUM(f.length_m), width_m: NUM(f.width_m), floors: NUM(f.floors) || 1,
        sokel_height_m: NUM(f.sokel_height_m), soffit_width_m: NUM(f.soffit_width_m),
        soffit_variant: f.soffit_variant,
        windows: f.windows.filter((w) => NUM(w.width_m) > 0 && NUM(w.count) > 0)
          .map((w) => ({ width_m: NUM(w.width_m), height_m: NUM(w.height_m), count: NUM(w.count), kind: w.kind, full_height: w.kind === "door" ? true : !!w.full_height })),
      };
      const { data } = await authApi().put(`/admin/leads/${lead.id}/precise`, { precise });
      onLeadUpdate({ ...lead, calc_input: { ...lead.calc_input, precise }, result: data.result });
      toast.success(`Prepočítané — nová cena ${fmt(data.result.total)}`);
    } catch { toast.error("Prepočet zlyhal"); }
    finally { setSaving(false); }
  };

  const v = lead.result?.precise;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid={`lead-precise-${lead.id}`}>
      <div>
        <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-3">Nahrané dokumenty</div>
        {(lead.plan_files || []).length === 0 && <p className="text-white/40 text-xs">Žiadne priložené súbory.</p>}
        <div className="space-y-2">
          {(lead.plan_files || []).map((file, i) => (
            <button key={i} onClick={() => download(file)} data-testid={`lead-file-${i}`}
              className="flex items-center gap-3 border border-white/15 hover:border-gold px-4 py-2.5 text-sm text-white/80 w-full text-left transition-colors">
              <Download className="w-4 h-4 text-gold shrink-0" />
              {file.name} <span className="text-white/35 text-xs">({file.kind === "floor_plan" ? "pôdorys" : "rezy"})</span>
            </button>
          ))}
        </div>
        {v && (
          <div className="mt-5">
            <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-2">Aktuálny výkaz</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-white/60 font-mono2">
              <span>Obvod: {v.dimensions?.perimeter_m} bm</span>
              <span>Výška: {v.dimensions?.height_m} m</span>
              <span>Hrubá plocha: {v.dimensions?.gross_area_m2} m²</span>
              <span>Čistá plocha: {v.net_area_m2 ?? "—"} m²</span>
              <span>Parapety: {v.sills?.bm} bm</span>
              <span>Špalety: {v.reveals?.bm} bm</span>
              <span>Sokel: {v.sokel?.area_m2} m²</span>
              <span>Podhľad: {v.soffit ? `${v.soffit.area_m2} m² (${v.soffit.variant})` : "—"}</span>
            </div>
          </div>
        )}
      </div>
      <div>
        <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-3">Upraviť výkaz (podľa pôdorysov)</div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[["length_m", "Dĺžka (m)"], ["width_m", "Šírka (m)"], ["floors", "Podlažia"], ["sokel_height_m", "Sokel (m)"], ["soffit_width_m", "Podhľad (m)"]].map(([k, lbl]) => (
            <NumField key={k} label={lbl} value={f[k]} testid={`lp-${k}`} onChange={(nv) => setF((x) => ({ ...x, [k]: nv }))} />
          ))}
          <div>
            <label className="block text-white/50 text-xs mb-1">Variant podhľadu</label>
            <select value={f.soffit_variant} onChange={(e) => setF((x) => ({ ...x, soffit_variant: e.target.value }))} data-testid="lp-soffit-variant" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-gold outline-none">
              <option value="battens">Laty po 25 cm</option><option value="osb">OSB debnenie</option><option value="koniky">Koníky</option>
            </select>
          </div>
        </div>
        <div className="space-y-1.5 mb-3">
          {f.windows.map((w, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs">
              <select value={w.kind} onChange={(e) => setW(i, "kind", e.target.value)} className="bg-ink border border-white/15 px-1.5 py-1.5" data-testid={`lp-win-kind-${i}`}><option value="window">Okno</option><option value="door">Dvere</option></select>
              <input type="number" step="0.1" value={w.width_m} onChange={(e) => setW(i, "width_m", e.target.value)} placeholder="š" className="w-16 bg-transparent border border-white/15 px-2 py-1.5 font-mono2" data-testid={`lp-win-w-${i}`} />
              <span className="text-white/40">×</span>
              <input type="number" step="0.1" value={w.height_m} onChange={(e) => setW(i, "height_m", e.target.value)} placeholder="v" className="w-16 bg-transparent border border-white/15 px-2 py-1.5 font-mono2" data-testid={`lp-win-h-${i}`} />
              <span className="text-white/40">×</span>
              <input type="number" value={w.count} onChange={(e) => setW(i, "count", e.target.value)} className="w-12 bg-transparent border border-white/15 px-2 py-1.5 font-mono2" data-testid={`lp-win-c-${i}`} />
              <span className="text-white/40">ks</span>
              {w.kind !== "door" && <label className="flex items-center gap-1 text-white/50"><input type="checkbox" checked={!!w.full_height} onChange={(e) => setW(i, "full_height", e.target.checked)} className="accent-[#D4AF37]" data-testid={`lp-win-full-${i}`} />od zeme</label>}
              <button onClick={() => setF((x) => ({ ...x, windows: x.windows.filter((_, idx) => idx !== i) }))} className="ml-auto text-white/30 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={() => setF((x) => ({ ...x, windows: [...x.windows, { width_m: "", height_m: "", count: 1, kind: "window", full_height: false }] }))} data-testid="lp-win-add"
            className="flex items-center gap-1.5 text-xs text-gold border border-gold/40 px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors"><Plus className="w-3 h-3" /> Pridať otvor</button>
        </div>
        <button onClick={recalc} disabled={saving} data-testid="lp-recalc"
          className="w-full bg-gold text-ink py-2.5 font-bold uppercase tracking-wider text-sm hover:bg-gold-light disabled:opacity-50 flex items-center justify-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Prepočítať a uložiť
        </button>
      </div>
    </div>
  );
}

function ProductsTab({ products, onAdd, onEdit, onDelete }) {
  const [zoom, setZoom] = useState(null);
  return (
    <div data-testid="products-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-4xl">Príslušenstvo na zateplenie</h2>
          <p className="text-white/40 text-sm mt-1">Objednávkový formulár pre partnerov. Foto, kód, popis, cena bez a s DPH.</p>
        </div>
        <button onClick={onAdd} data-testid="add-product-btn" className="flex items-center gap-2 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light"><Plus className="w-4 h-4" /> Pridať produkt</button>
      </div>
      {products.length === 0 && <p className="text-white/40 border border-white/10 p-10 text-center">Zatiaľ žiadne produkty.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-list">
        {products.map((p) => (
          <div key={p.id} className="border border-white/10 bg-ink-2 flex flex-col">
            <button onClick={() => p.image && setZoom(p.image)} className="aspect-[4/3] overflow-hidden bg-ink border-b border-white/10 group">
              {p.image ? <img src={imgUrl(p.image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center text-white/20"><Boxes className="w-8 h-8" /></div>}
            </button>
            <div className="p-4 flex flex-col gap-1 flex-1">
              <div className="flex items-center justify-between">
                <span className="font-mono2 text-gold text-[11px] uppercase tracking-wider">{p.code}</span>
                <span className={`text-[10px] px-2 py-0.5 ${p.active ? "bg-gold/20 text-gold" : "bg-white/10 text-white/50"}`}>{p.active ? "Aktívny" : "Skrytý"}</span>
              </div>
              <span className="text-white text-sm font-semibold leading-tight">{p.name}</span>
              <span className="text-white/40 text-xs leading-snug flex-1">{p.description}</span>
              <div className="flex items-baseline justify-between mt-2">
                <span className="text-white/50 text-xs">{p.price_no_vat?.toFixed(2)} € bez DPH</span>
                <span className="text-gold font-bold">{p.price_vat?.toFixed(2)} € s DPH</span>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => onEdit(p)} data-testid={`edit-product-${p.code}`} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-white/20 hover:border-gold text-sm"><Edit3 className="w-4 h-4" /> Upraviť</button>
                <button onClick={() => onDelete(p.id)} className="px-3 py-2 border border-white/20 hover:border-red-500 text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </div>
  );
}

function ProductEditor({ product, onClose, onSave, vatPct = 23 }) {
  const [f, setF] = useState({
    id: product.id, code: product.code || "", name: product.name || "", description: product.description || "",
    price_no_vat: product.price_no_vat ?? "", price_vat: product.price_vat ?? "", image: product.image || "",
    images: product.images?.length ? product.images : (product.image ? [{ url: product.image, alt: product.name || "" }] : []),
    datasheet_url: product.datasheet_url || "", datasheet_name: product.datasheet_name || "",
    slug: product.slug || "", meta_title: product.meta_title || "", meta_description: product.meta_description || "",
    active: product.active ?? true,
  });
  const [dsBusy, setDsBusy] = useState(false);
  const dsRef = useRef();
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const vat = vatPct || 23;
  const toVat = (v) => Math.round(NUM(v) * (1 + vat / 100) * 100) / 100;
  const toNoVat = (v) => Math.round((NUM(v) / (1 + vat / 100)) * 100) / 100;
  const slugifyJs = (s) => (s || "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const addImage = (url) => setF((x) => ({ ...x, images: [...x.images, { url, alt: x.name || "" }] }));
  const setImgAlt = (i, alt) => setF((x) => { const im = [...x.images]; im[i] = { ...im[i], alt }; return { ...x, images: im }; });
  const delImage = (i) => setF((x) => ({ ...x, images: x.images.filter((_, j) => j !== i) }));

  const uploadDatasheet = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Technický list musí byť PDF.");
    if (file.size > 8 * 1024 * 1024) return toast.error("Súbor je príliš veľký (max 8 MB).");
    setDsBusy(true);
    try {
      const url = await uploadImage(file);
      setF((x) => ({ ...x, datasheet_url: url, datasheet_name: file.name }));
      toast.success("Technický list nahraný");
    } catch { toast.error("Nahrávanie zlyhalo"); } finally { setDsBusy(false); if (dsRef.current) dsRef.current.value = ""; }
  };

  const genSeo = () => setF((x) => ({
    ...x,
    slug: slugifyJs(x.name || x.code),
    meta_title: `${x.name} – cena a technické parametre | Zateplenia.eu`,
    meta_description: `${(x.description || `${x.name} z našej ponuky zatepľovacích systémov.`)}${x.price_vat ? ` Cena ${x.price_vat} € s DPH.` : ""}`.slice(0, 155),
  }));

  const submit = () => {
    if (!f.code || !f.name) return toast.error("Zadajte kód a názov produktu.");
    onSave({ ...f, price_no_vat: NUM(f.price_no_vat), price_vat: NUM(f.price_vat), image: f.images[0]?.url || f.image || "" });
  };
  return (
    <div className="fixed inset-0 z-[200] bg-ink/90 backdrop-blur-md overflow-y-auto p-4 md:p-8" data-testid="product-editor">
      <div className="max-w-2xl mx-auto bg-ink-2 border border-white/10 p-6 md:p-8">
        <div className="flex items-center justify-between mb-6"><h3 className="font-display text-3xl">{f.id ? "Upraviť produkt" : "Nový produkt"}</h3><button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-6 h-6" /></button></div>
        <div className="space-y-5">
          <div>
            <label className="block text-white/50 text-xs mb-2">Fotky produktu (s alt textami pre vyhľadávače a AI)</label>
            <div className="space-y-2">
              {f.images.map((img, i) => (
                <div key={i} className="flex items-center gap-3 border border-white/10 p-2" data-testid={`pe-image-row-${i}`}>
                  <div className="w-12 h-12 shrink-0 bg-ink border border-white/15 overflow-hidden">
                    <img src={imgUrl(img.url)} alt={img.alt || ""} className="w-full h-full object-cover" />
                  </div>
                  <input value={img.alt} onChange={(e) => setImgAlt(i, e.target.value)} placeholder="Alt text (napr. Balenie lepidla 25 kg)…" data-testid={`pe-image-alt-${i}`}
                    className="flex-1 bg-transparent border border-white/15 px-3 py-1.5 text-sm focus:border-gold outline-none" />
                  <button onClick={() => delImage(i)} data-testid={`pe-image-del-${i}`} className="p-2 border border-white/20 text-red-400 hover:border-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="mt-2"><ImageUpload value="" testid="pe-product-image" label="Pridať fotku" onUploaded={addImage} /></div>
            <div className="text-white/30 text-xs mt-1">Prvá fotka je titulná. Alt texty čítajú Google aj AI vyhľadávače.</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Kód produktu *" value={f.code} onChange={(v) => set("code", v)} testid="pe-product-code" />
            <TextField label="Názov *" value={f.name} onChange={(v) => set("name", v)} testid="pe-product-name" />
            <NumField label="Cena bez DPH (€)" step="0.01" value={f.price_no_vat} onChange={(v) => setF((x) => ({ ...x, price_no_vat: v, price_vat: toVat(v) }))} testid="pe-product-novat" />
            <NumField label={`Cena s DPH ${vat} % (€)`} step="0.01" value={f.price_vat} onChange={(v) => setF((x) => ({ ...x, price_vat: v, price_no_vat: toNoVat(v) }))} testid="pe-product-vat" />
          </div>
          <div className="text-white/30 text-xs -mt-2">Ceny sa prepočítavajú obojsmerne podľa sadzby DPH {vat} %.</div>
          <div><label className="block text-white/50 text-xs mb-1">Popis</label><textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="pe-product-desc" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm resize-none focus:border-gold outline-none" /></div>
          <div>
            <label className="block text-white/50 text-xs mb-2">Technický list (PDF)</label>
            <input ref={dsRef} type="file" accept="application/pdf" onChange={uploadDatasheet} className="hidden" data-testid="pe-datasheet-input" />
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => dsRef.current?.click()} disabled={dsBusy} data-testid="pe-datasheet-btn"
                className="flex items-center gap-2 border border-white/20 hover:border-gold text-white/80 hover:text-gold px-4 py-2 text-sm transition-colors disabled:opacity-50">
                {dsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Nahrať technický list
              </button>
              {f.datasheet_url && (
                <span className="flex items-center gap-2 text-sm text-gold">
                  <FileText className="w-4 h-4" />
                  <a href={imgUrl(f.datasheet_url)} target="_blank" rel="noreferrer" className="hover:underline" data-testid="pe-datasheet-link">{f.datasheet_name || "Technický list"}</a>
                  <button onClick={() => setF((x) => ({ ...x, datasheet_url: "", datasheet_name: "" }))} data-testid="pe-datasheet-del" className="text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>
                </span>
              )}
            </div>
          </div>
          <div className="border border-white/10 p-4 space-y-3" data-testid="pe-seo-section">
            <div className="flex items-center justify-between">
              <label className="text-white/50 text-xs uppercase tracking-wider font-mono2">SEO polia (vyhľadávače a AI)</label>
              <button type="button" onClick={genSeo} data-testid="pe-seo-generate" className="flex items-center gap-1.5 text-gold text-sm hover:text-gold-light"><Sparkles className="w-4 h-4" /> Vygenerovať automaticky</button>
            </div>
            <TextField label="Slug (URL)" value={f.slug} onChange={(v) => set("slug", v)} testid="pe-product-slug" />
            <TextField label="Meta title" value={f.meta_title} onChange={(v) => set("meta_title", v)} testid="pe-product-meta-title" />
            <div><label className="block text-white/50 text-xs mb-1">Meta description ({(f.meta_description || "").length}/155)</label>
              <textarea value={f.meta_description} onChange={(e) => set("meta_description", e.target.value)} rows={2} maxLength={200} data-testid="pe-product-meta-desc" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm resize-none focus:border-gold outline-none" /></div>
          </div>
          <label className="flex items-center gap-3 text-white/70 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="pe-product-active" className="w-5 h-5 accent-gold" /> Aktívny (viditeľný v objednávke)</label>
        </div>
        <div className="flex gap-3 mt-6"><button onClick={submit} data-testid="pe-product-save" className="flex-1 bg-gold text-ink py-3 font-bold uppercase tracking-wider hover:bg-gold-light">Uložiť produkt</button><button onClick={onClose} className="px-6 py-3 border border-white/20 hover:border-white/50">Zrušiť</button></div>
      </div>
    </div>
  );
}

function ReviewsTab({ reviews, onCreate, onDelete, onApprove }) {
  const empty = { name: "", town: "", rating: 5, text: "", date: "" };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim() || !form.text.trim()) { toast.error("Vyplňte meno a text recenzie."); return; }
    const ok = await onCreate({ ...form, rating: Number(form.rating) });
    if (ok) setForm(empty);
  };
  const fmt = (d) => { try { return new Date(d).toLocaleDateString("sk-SK", { day: "numeric", month: "long", year: "numeric" }); } catch { return d; } };
  return (
    <div className="space-y-8" data-testid="reviews-tab">
      <section className="border border-gold/20 bg-ink-2 p-6">
        <h2 className="font-display text-3xl mb-1">Pridať recenziu</h2>
        <p className="text-white/40 text-sm mb-5">Pridávajte len reálne hodnotenia od zákazníkov. Zobrazia sa na hlavnej stránke aj v štruktúrovaných dátach (hviezdičky v Google/AI).</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Meno zákazníka" data-testid="review-name" className="bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-2 text-sm" />
          <input value={form.town} onChange={(e) => set("town", e.target.value)} placeholder="Mesto / okres (voliteľné)" data-testid="review-town" className="bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-2 text-sm" />
          <select value={form.rating} onChange={(e) => set("rating", e.target.value)} data-testid="review-rating" className="bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-2 text-sm">
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
          </select>
          <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="review-date" className="bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-2 text-sm" />
        </div>
        <textarea value={form.text} onChange={(e) => set("text", e.target.value)} placeholder="Text recenzie" rows={3} data-testid="review-text" className="w-full mt-4 bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-2 text-sm" />
        <button onClick={submit} data-testid="review-submit" className="mt-4 flex items-center gap-2 bg-gold text-ink px-6 py-2.5 font-bold uppercase tracking-wider text-sm hover:bg-gold-light"><Plus className="w-4 h-4" /> Pridať recenziu</button>
      </section>

      <div className="space-y-3">
        <h2 className="font-display text-3xl mb-2">Recenzie ({reviews.length})</h2>
        {reviews.some((r) => !r.approved) && <p className="text-gold text-sm">Máte recenzie čakajúce na schválenie — potvrďte ich, aby sa zobrazili na webe.</p>}
        {reviews.length === 0 && <p className="text-white/40 border border-white/10 p-8 text-center">Zatiaľ žiadne recenzie. Pridajte prvé reálne hodnotenie vyššie.</p>}
        {reviews.map((r) => (
          <div key={r.id} className={`border p-5 flex items-start justify-between gap-4 ${r.approved ? "border-white/10 bg-ink-2" : "border-gold/40 bg-gold/[0.04]"}`} data-testid={`admin-review-${r.id}`}>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-white font-semibold">{r.name}</span>
                <span className="text-gold text-sm">{"★".repeat(r.rating)}<span className="text-white/20">{"★".repeat(5 - r.rating)}</span></span>
                {r.approved
                  ? <span className="text-emerald-400 text-[11px] uppercase tracking-widest font-mono2">Zverejnená</span>
                  : <span className="text-gold text-[11px] uppercase tracking-widest font-mono2">Čaká na schválenie</span>}
                {r.source === "client" && <span className="text-white/30 text-[11px]">(od klienta)</span>}
                {r.partner_name && <span className="text-[10px] uppercase tracking-widest font-mono2 border border-gold/40 text-gold px-2 py-0.5">firma: {r.partner_name}</span>}
              </div>
              <div className="text-white/40 text-xs mt-0.5">{r.town ? `${r.town} · ` : ""}{fmt(r.date)}</div>
              <p className="text-white/70 text-sm mt-2">{r.text}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => onApprove(r.id, !r.approved)} data-testid={`review-approve-${r.id}`}
                className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border ${r.approved ? "border-white/20 text-white/60 hover:border-white/40" : "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"}`}>
                {r.approved ? "Skryť" : "Zverejniť"}
              </button>
              <button onClick={() => onDelete(r.id)} data-testid={`review-delete-${r.id}`} className="p-2 border border-white/20 hover:border-red-500 text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function OrdersTab({ orders, onStatus }) {
  return (
    <div className="space-y-4" data-testid="orders-tab">
      <h2 className="font-display text-4xl mb-2">Objednávky príslušenstva</h2>
      {orders.length === 0 && <p className="text-white/40 border border-white/10 p-10 text-center">Zatiaľ žiadne objednávky.</p>}
      {orders.map((o) => (
        <div key={o.id} className="border border-white/10 bg-ink-2 p-5" data-testid={`order-${o.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-display text-2xl text-white">{o.partner_name}</div>
              <div className="text-white/40 text-xs">{o.partner_email} · {o.partner_phone || "—"} · {new Date(o.created_at).toLocaleString("sk-SK")}</div>
              {o.delivery && <div className="text-white/50 text-xs mt-1">Dodanie: {o.delivery}</div>}
            </div>
            <div className="text-right">
              <div className="text-white/50 text-xs">{o.total_no_vat?.toFixed(2)} € bez DPH</div>
              <div className="font-mono2 text-gold text-xl">{o.total_vat?.toFixed(2)} € s DPH</div>
              <select value={o.status} onChange={(e) => onStatus(o.id, e.target.value)} data-testid={`order-status-${o.id}`} className="bg-ink border border-white/20 px-2 py-1 text-xs mt-2">
                <option value="new">Nová</option><option value="processing">Spracúva sa</option><option value="done">Vybavená</option><option value="cancelled">Zrušená</option>
              </select>
            </div>
          </div>
          <div className="mt-4 border-t border-white/10 pt-3 space-y-1">
            {o.items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm text-white/70">
                <span><span className="font-mono2 text-gold text-xs">{it.code}</span> · {it.name} × {it.qty}</span>
                <span className="font-mono2">{(it.price_vat * it.qty).toFixed(2)} €</span>
              </div>
            ))}
            {o.note && <div className="text-white/40 text-xs mt-2">Poznámka: {o.note}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

const INS_STATUS = [["nova", "Nová"], ["kontaktovana", "Kontaktovaná"], ["realizovana", "Realizovaná"], ["zrusena", "Zrušená"]];
const INS_STATUS_COLOR = { nova: "text-gold", kontaktovana: "text-blue-400", realizovana: "text-green-400", zrusena: "text-white/40" };
const INS_LABEL = Object.fromEntries(INS_STATUS);
const ORIENT_EUR_M2 = 55; // orientačná cena „od" podľa kalkulačky (s DPH) — fallback pre objednávky bez ceny

function exportOrdersCsv(rows) {
  const cols = [
    ["created_at", "Dátum"], ["name", "Meno"], ["email", "E-mail"], ["phone", "Telefón"],
    ["object_address", "Objekt"], ["object_type", "Typ"], ["area", "Plocha (m2)"],
    ["material", "Materiál"], ["thickness", "Hrúbka (mm)"], ["plaster", "Omietka"],
    ["term", "Termín"], ["price", "Orient. cena (€)"], ["status", "Stav"], ["customer_address", "Adresa objednávateľa"], ["note", "Poznámka"],
  ];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = cols.map(([, l]) => esc(l)).join(";");
  const body = rows.map((o) => cols.map(([k]) => {
    if (k === "status") return esc(INS_LABEL[o.status] || o.status || "Nová");
    if (k === "created_at") return esc(o.created_at ? new Date(o.created_at).toLocaleString("sk-SK") : "");
    return esc(o[k]);
  }).join(";")).join("\n");
  const blob = new Blob(["\uFEFF" + head + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `objednavky-zateplenie-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const XLSX_COLS = [
  ["created_at", "Dátum", 20], ["name", "Meno", 24], ["email", "E-mail", 24], ["phone", "Telefón", 16],
  ["object_address", "Objekt", 28], ["object_type", "Typ", 16], ["area", "Plocha (m²)", 12],
  ["material", "Materiál", 24], ["thickness", "Hrúbka (mm)", 12], ["plaster", "Omietka", 20],
  ["term", "Termín", 16], ["price", "Orient. cena (€)", 16], ["status", "Stav", 14], ["customer_address", "Adresa objednávateľa", 28], ["note", "Poznámka", 30],
];

async function exportOrdersXlsx(rows) {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Objednávky");
  ws.columns = XLSX_COLS.map(([key, header, width]) => ({ key, header, width }));
  const hr = ws.getRow(1);
  hr.font = { bold: true, color: { argb: "FF0A0A0A" } };
  hr.eachCell((c) => { c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD4AF37" } }; });
  let totalArea = 0;
  rows.forEach((o) => {
    const area = parseFloat(String(o.area).replace(",", ".")) || 0;
    if ((o.status || "nova") !== "zrusena") totalArea += area;
    ws.addRow({
      created_at: o.created_at ? new Date(o.created_at).toLocaleString("sk-SK") : "",
      name: o.name || "", email: o.email || "", phone: o.phone || "",
      object_address: o.object_address || "", object_type: o.object_type || "",
      area: area || "", material: o.material || "", thickness: o.thickness || "",
      plaster: o.plaster || "", term: o.term || "", price: o.price || "", status: INS_LABEL[o.status] || o.status || "Nová",
      customer_address: o.customer_address || "", note: o.note || "",
    });
  });
  ws.addRow({});
  const t = ws.addRow({ name: "SPOLU (bez zrušených):", area: totalArea, material: `~${Math.round(totalArea * ORIENT_EUR_M2).toLocaleString("sk-SK")} € (55 €/m²)` });
  t.font = { bold: true };
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `objednavky-zateplenie-${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

function InsulationOrdersTab({ orders, onStatus, onDelete }) {
  const [fStatus, setFStatus] = useState("all");
  const [q, setQ] = useState("");
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");
  const count = (s) => orders.filter((o) => (o.status || "nova") === s).length;
  const totalArea = orders
    .filter((o) => (o.status || "nova") !== "zrusena")
    .reduce((sum, o) => sum + (parseFloat(String(o.area).replace(",", ".")) || 0), 0);
  const totalValue = orders
    .filter((o) => (o.status || "nova") !== "zrusena")
    .reduce((sum, o) => {
      const p = parseFloat(String(o.price).replace(",", ".")) || 0;
      if (p > 0) return sum + p;
      const a = parseFloat(String(o.area).replace(",", ".")) || 0;
      return sum + a * ORIENT_EUR_M2;
    }, 0);
  const cards = [
    ["Nové", count("nova"), "text-gold"],
    ["Kontaktované", count("kontaktovana"), "text-blue-400"],
    ["Realizované", count("realizovana"), "text-green-400"],
    ["Plocha v dopyte", `${Math.round(totalArea).toLocaleString("sk-SK")} m²`, "text-white"],
    ["Hodnota dopytu", `${Math.round(totalValue).toLocaleString("sk-SK")} €`, "text-gold"],
  ];
  const filtered = orders.filter((o) => {
    if (fStatus !== "all" && (o.status || "nova") !== fStatus) return false;
    if (q) {
      const hay = `${o.name || ""} ${o.object_address || ""} ${o.customer_address || ""} ${o.email || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    const day = (o.created_at || "").slice(0, 10);
    if (dFrom && day < dFrom) return false;
    if (dTo && day > dTo) return false;
    return true;
  });
  const [emailModal, setEmailModal] = useState(null); // {id, name, status} | null
  const [msgText, setMsgText] = useState("");
  const onStatusChange = (o, newStatus) => {
    if (newStatus === o.status) return;
    if ((newStatus === "kontaktovana" || newStatus === "realizovana") && o.email) {
      setMsgText("");
      setEmailModal({ id: o.id, name: o.name, status: newStatus });
    } else {
      onStatus(o.id, newStatus, "", false);
    }
  };
  return (
    <div className="space-y-4" data-testid="insulation-orders-tab">
      <h2 className="font-display text-4xl mb-2">Objednávky zateplenia ({orders.length})</h2>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ins-order-dashboard">
        {cards.map(([label, val, color]) => (
          <div key={label} className="border border-white/10 bg-ink-2 p-4">
            <div className="text-white/40 text-[10px] font-mono2 uppercase tracking-widest">{label}</div>
            <div className={`font-display text-3xl mt-1 ${color}`}>{val}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 border border-white/10 bg-ink-2 p-4" data-testid="ins-order-filters">
        <div className="flex-1 min-w-[200px]">
          <label className="text-white/40 text-[10px] font-mono2 uppercase tracking-widest">Hľadať (meno, mesto, e-mail)</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ins-filter-search" placeholder="napr. Bratislava…"
            className="mt-1 w-full bg-ink border border-white/20 px-3 py-2 text-sm text-white focus:border-gold outline-none" />
        </div>
        <div>
          <label className="text-white/40 text-[10px] font-mono2 uppercase tracking-widest">Stav</label>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="ins-filter-status"
            className="mt-1 block bg-ink border border-white/20 px-3 py-2 text-sm text-white">
            <option value="all">Všetky</option>
            {INS_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-white/40 text-[10px] font-mono2 uppercase tracking-widest">Od dátumu</label>
          <input type="date" value={dFrom} onChange={(e) => setDFrom(e.target.value)} data-testid="ins-filter-from"
            className="mt-1 block bg-ink border border-white/20 px-3 py-2 text-sm text-white" />
        </div>
        <div>
          <label className="text-white/40 text-[10px] font-mono2 uppercase tracking-widest">Do dátumu</label>
          <input type="date" value={dTo} onChange={(e) => setDTo(e.target.value)} data-testid="ins-filter-to"
            className="mt-1 block bg-ink border border-white/20 px-3 py-2 text-sm text-white" />
        </div>
        {(q || fStatus !== "all" || dFrom || dTo) && (
          <button onClick={() => { setQ(""); setFStatus("all"); setDFrom(""); setDTo(""); }} data-testid="ins-filter-clear"
            className="px-3 py-2 border border-white/20 text-white/60 hover:border-gold hover:text-gold text-xs uppercase tracking-widest">
            Zrušiť filter
          </button>
        )}
        <button onClick={() => exportOrdersCsv(filtered)} disabled={filtered.length === 0} data-testid="ins-export-csv"
          className="inline-flex items-center gap-2 px-3 py-2 border border-gold/40 text-gold hover:bg-gold hover:text-ink text-xs uppercase tracking-widest disabled:opacity-40 transition-colors">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
        <button onClick={() => exportOrdersXlsx(filtered)} disabled={filtered.length === 0} data-testid="ins-export-xlsx"
          className="inline-flex items-center gap-2 px-3 py-2 border border-green-500/40 text-green-400 hover:bg-green-500 hover:text-ink text-xs uppercase tracking-widest disabled:opacity-40 transition-colors">
          <Download className="w-3.5 h-3.5" /> Export Excel
        </button>
      </div>

      {orders.length > 0 && (
        <p className="text-white/40 text-xs" data-testid="ins-order-count">Zobrazené {filtered.length} z {orders.length}</p>
      )}

      {orders.length === 0 && <p className="text-white/40 border border-white/10 p-10 text-center">Zatiaľ žiadne objednávky zateplenia.</p>}
      {orders.length > 0 && filtered.length === 0 && <p className="text-white/40 border border-white/10 p-10 text-center">Žiadne objednávky nezodpovedajú filtru.</p>}
      {filtered.map((o) => (
        <div key={o.id} className="border border-white/10 bg-ink-2 p-5" data-testid={`ins-order-${o.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-display text-2xl text-white">{o.name}</div>
              <div className="text-white/40 text-xs">
                {o.email || "—"} · {o.phone || "—"} · {new Date(o.created_at).toLocaleString("sk-SK")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {o.has_pdf && (
                <a
                  href={`${api.defaults.baseURL}/insulation-order/${o.id}/pdf?t=${o.pdf_token}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`ins-order-pdf-${o.id}`}
                  className="inline-flex items-center gap-1 border border-gold/40 text-gold px-3 py-1.5 text-xs hover:bg-gold hover:text-ink transition-colors"
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
              )}
              <select value={o.status || "nova"} onChange={(e) => onStatusChange(o, e.target.value)}
                data-testid={`ins-order-status-${o.id}`}
                className={`bg-ink border border-white/20 px-2 py-1.5 text-xs ${INS_STATUS_COLOR[o.status] || "text-white"}`}>
                {INS_STATUS.map(([v, l]) => <option key={v} value={v} className="text-white">{l}</option>)}
              </select>
              <button onClick={() => onDelete(o.id)} data-testid={`ins-order-delete-${o.id}`}
                className="p-1.5 border border-white/20 text-white/50 hover:border-red-500 hover:text-red-400 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="mt-4 border-t border-white/10 pt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            <Field label="Objekt" value={o.object_address} />
            <Field label="Typ" value={o.object_type} />
            <Field label="Plocha" value={o.area ? `${o.area} m²` : ""} />
            <Field label="Materiál" value={o.material} />
            <Field label="Hrúbka" value={o.thickness ? `${o.thickness} mm` : ""} />
            <Field label="Omietka" value={o.plaster} />
            <Field label="Termín" value={o.term} />
            <Field label="Orient. cena" value={o.price ? `${Number(o.price).toLocaleString("sk-SK")} €` : ""} />
            <Field label="Adresa objednávateľa" value={o.customer_address} />
          </div>
          {o.note && <div className="text-white/40 text-xs mt-3">Poznámka: {o.note}</div>}
        </div>
      ))}

      {emailModal && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ins-email-modal">
          <div className="bg-ink border border-gold/30 max-w-lg w-full p-6">
            <h3 className="font-display text-2xl text-white mb-1">
              Poslať e-mail zákazníkovi{emailModal.name ? ` (${emailModal.name})` : ""}
            </h3>
            <p className="text-white/50 text-sm mb-4">
              Stav sa zmení na „{INS_LABEL[emailModal.status]}". Môžete pridať krátky osobný odkaz (nepovinné), napr. návrh termínu obhliadky.
            </p>
            <textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} rows={4} data-testid="ins-email-message"
              placeholder="Napr.: Dobrý deň, obhliadku navrhujeme na utorok 14:00. Ozvite sa nám prosím…"
              className="w-full bg-ink-2 border border-white/15 px-4 py-3 text-white text-sm focus:border-gold outline-none resize-none" />
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <button
                onClick={() => { onStatus(emailModal.id, emailModal.status, msgText, true); setEmailModal(null); toast.success("Stav zmenený, e-mail odoslaný."); }}
                data-testid="ins-email-send"
                className="flex-1 bg-gold text-ink px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-gold-light transition-colors">
                Odoslať e-mail
              </button>
              <button
                onClick={() => { onStatus(emailModal.id, emailModal.status, "", false); setEmailModal(null); toast.success("Stav zmenený (bez e-mailu)."); }}
                data-testid="ins-email-skip"
                className="flex-1 border border-white/25 text-white/70 px-5 py-3 font-bold uppercase tracking-widest text-xs hover:border-white/50 transition-colors">
                Zmeniť bez e-mailu
              </button>
              <button
                onClick={() => setEmailModal(null)}
                data-testid="ins-email-cancel"
                className="px-4 py-3 text-white/40 hover:text-white text-xs uppercase tracking-widest">
                Zrušiť
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-white/35 text-[10px] font-mono2 uppercase tracking-widest">{label}</div>
      <div className="text-white/80">{value || "—"}</div>
    </div>
  );
}


function LeadFirmsTab({ firms, districts, onCreate, onBulk, onUpdate, onDelete }) {
  const empty = { company: "", email: "", phone: "", website: "", country: "SK", district: "", city: "", source: "manuálne", active: true };
  const [form, setForm] = useState(empty);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [q, setQ] = useState("");
  const [editFirm, setEditFirm] = useState(null); // firm object being edited | null
  const opts = districts?.[form.country] || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.email || !form.district) { toast.error("Vyplňte názov, e-mail a okres."); return; }
    const ok = await onCreate(form);
    if (ok) setForm({ ...empty, country: form.country });
  };

  const doBulk = async () => {
    // formát riadku: Názov; email; telefón; okres; SK/CZ; web
    const items = bulkText.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
      const p = line.split(/[;\t]/).map((x) => x.trim());
      return { company: p[0] || "", email: (p[1] || "").toLowerCase(), phone: p[2] || "", district: p[3] || "", country: (p[4] || "SK").toUpperCase(), website: p[5] || "", source: "import", active: true };
    }).filter((x) => x.company && x.email && x.district);
    if (!items.length) { toast.error("Žiadne platné riadky. Formát: Názov; email; telefón; okres; SK/CZ; web"); return; }
    const ok = await onBulk(items);
    if (ok) { setBulkText(""); setShowBulk(false); }
  };

  const list = firms.filter((f) => {
    if (fCountry && f.country !== fCountry) return false;
    if (fStatus === "pending" && f.approved) return false;
    if (fStatus === "active" && (!f.active || !f.approved || f.unsubscribed)) return false;
    if (fStatus === "unsub" && !f.unsubscribed) return false;
    if (fStatus === "inactive" && (f.active || f.unsubscribed || !f.approved)) return false;
    if (fStatus === "featured" && !f.featured) return false;
    if (q) { const s = `${f.company} ${f.email} ${f.district} ${f.city}`.toLowerCase(); if (!s.includes(q.toLowerCase())) return false; }
    return true;
  });

  const totals = {
    all: firms.length,
    pending: firms.filter((f) => !f.approved && !f.unsubscribed).length,
    active: firms.filter((f) => f.active && f.approved && !f.unsubscribed).length,
    unsub: firms.filter((f) => f.unsubscribed).length,
    interested: firms.reduce((s, f) => s + (f.interested_count || 0), 0),
  };

  return (
    <div className="space-y-6" data-testid="lead-firms-tab">
      <div>
        <h2 className="font-display text-4xl mb-1">Realizačné firmy – distribúcia dopytov</h2>
        <p className="text-white/40 text-sm max-w-2xl">Keď príde nový dopyt, firmám v danom okrese pošleme <b>náhľad</b> (okres, plocha, materiál, termín) a odkaz „Mám záujem" – kontakt sa im zobrazí až po kliknutí. Max <b>2 e-maily/týždeň</b> na firmu, zvyšok súhrnne. Importované firmy <b>čakajú na schválenie</b> a dopyty im začnú chodiť až po schválení. Odhlásenie jedným klikom.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="firm-stats">
        {[["Firmy", totals.all, "text-gold"], ["Čaká na schválenie", totals.pending, "text-amber-400"], ["Aktívne", totals.active, "text-green-400"], ["Odhlásené", totals.unsub, "text-red-400"], ["Prejavený záujem", totals.interested, "text-gold"]].map(([l, v, c]) => (
          <div key={l} className="bg-ink-2 border border-white/10 p-4">
            <div className="text-white/40 text-[10px] font-mono2 uppercase tracking-widest">{l}</div>
            <div className={`text-2xl font-display ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="bg-ink-2 border border-white/10 p-5 space-y-3" data-testid="firm-add-form">
        <div className="flex items-center gap-2 text-gold font-mono2 text-xs uppercase tracking-widest"><Plus className="w-4 h-4" /> Pridať firmu (schválená automaticky)</div>
        <div className="grid md:grid-cols-3 gap-3">
          <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Názov firmy *" data-testid="firm-company" className="bg-ink border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-mail *" data-testid="firm-email" className="bg-ink border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none" />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefón" data-testid="firm-phone" className="bg-ink border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none" />
          <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value, district: "" })} data-testid="firm-country" className="bg-ink border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none">
            <option value="SK">Slovensko</option>
            <option value="CZ">Česko</option>
          </select>
          <select value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="firm-district" className="bg-ink border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none">
            <option value="">Okres *</option>
            {opts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Web (nepovinné)" data-testid="firm-website" className="bg-ink border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none" />
        </div>
        <div className="flex flex-wrap gap-3">
          <button type="submit" data-testid="firm-add-submit" className="bg-gold text-ink px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-gold-light transition-colors">Pridať firmu</button>
          <button type="button" onClick={() => setShowBulk((s) => !s)} data-testid="firm-bulk-toggle" className="border border-white/25 text-white/70 px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:border-gold/50 transition-colors">Hromadný import (na schválenie)</button>
        </div>
        {showBulk && (
          <div className="pt-2 space-y-2" data-testid="firm-bulk-box">
            <p className="text-white/40 text-xs">Jeden riadok = jedna firma. Formát: <code className="text-gold">Názov; email; telefón; okres; SK/CZ; web</code>. Importované firmy čakajú na schválenie.</p>
            <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6} data-testid="firm-bulk-text" placeholder="Fasády Novák s.r.o.; info@fasadynovak.sk; 0900123456; Trnava; SK; fasadynovak.sk" className="w-full bg-ink border border-white/15 px-3 py-2 text-white text-sm font-mono2 focus:border-gold outline-none resize-none" />
            <button type="button" onClick={doBulk} data-testid="firm-bulk-submit" className="bg-gold text-ink px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-gold-light transition-colors">Importovať</button>
          </div>
        )}
      </form>

      <div className="flex flex-wrap gap-3 items-center" data-testid="firm-filters">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hľadať…" data-testid="firm-search" className="bg-ink border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none" />
        <select value={fCountry} onChange={(e) => setFCountry(e.target.value)} data-testid="firm-filter-country" className="bg-ink border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none">
          <option value="">Všetky krajiny</option><option value="SK">Slovensko</option><option value="CZ">Česko</option>
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="firm-filter-status" className="bg-ink border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none">
          <option value="">Všetky stavy</option><option value="pending">Čaká na schválenie</option><option value="active">Aktívne</option><option value="inactive">Neaktívne</option><option value="featured">Topované</option><option value="unsub">Odhlásené</option>
        </select>
        <span className="text-white/40 text-sm">{list.length} firiem</span>
      </div>

      <div className="space-y-2">
        {list.map((f) => {
          const pending = !f.approved && !f.unsubscribed;
          return (
          <div key={f.id} className={`bg-ink-2 border p-4 flex flex-col md:flex-row md:items-center gap-3 ${f.featured ? "border-gold/50" : pending ? "border-amber-400/40" : "border-white/10"}`} data-testid={`firm-row-${f.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {f.featured && <Star className="w-4 h-4 text-gold fill-gold" data-testid={`firm-featured-badge-${f.id}`} />}
                <span className="text-white font-bold">{f.company}</span>
                <span className="text-[10px] font-mono2 uppercase tracking-widest border border-white/20 text-white/50 px-1.5 py-0.5">{f.country}</span>
                <span className="text-gold text-xs">{f.district}</span>
                {pending && <span className="text-[10px] font-mono2 uppercase tracking-widest bg-amber-400/15 text-amber-400 px-1.5 py-0.5">čaká na schválenie</span>}
                {f.card_submitted && !f.card_approved && <span className="text-[10px] font-mono2 uppercase tracking-widest bg-blue-400/15 text-blue-300 px-1.5 py-0.5">vizitka na schválenie</span>}
                {f.card_approved && <a href={`/firma/${f.slug}`} target="_blank" rel="noreferrer" className="text-[10px] font-mono2 uppercase tracking-widest bg-green-500/15 text-green-400 px-1.5 py-0.5 hover:underline">vizitka zverejnená ↗</a>}
                {f.unsubscribed && <span className="text-[10px] font-mono2 uppercase tracking-widest bg-red-500/15 text-red-400 px-1.5 py-0.5 flex items-center gap-1"><Ban className="w-3 h-3" />odhlásená</span>}
              </div>
              <div className="text-white/50 text-sm mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{f.email}</span>
                {f.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{f.phone}</span>}
                {f.website && <a href={f.website.startsWith("http") ? f.website : `https://${f.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-gold"><ExternalLink className="w-3 h-3" />{f.website}</a>}
                {f.source && <span className="text-white/25">zdroj: {f.source}</span>}
              </div>
            </div>
            <div className="flex items-center gap-4 text-center shrink-0">
              <div><div className="text-gold font-display text-xl flex items-center gap-1"><Send className="w-3.5 h-3.5" />{f.notified_count || 0}</div><div className="text-white/30 text-[10px] uppercase">odoslané</div></div>
              <div><div className="text-green-400 font-display text-xl">{f.interested_count || 0}</div><div className="text-white/30 text-[10px] uppercase">záujem</div></div>
            </div>
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              {pending && (
                <button onClick={() => onUpdate(f.id, { approved: true, active: true })} data-testid={`firm-approve-${f.id}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border border-amber-400/50 text-amber-400 hover:bg-amber-400/10 transition-colors">
                  <Check className="w-3.5 h-3.5" /> Schváliť
                </button>
              )}
              {f.card_submitted && !f.card_approved && (
                <button onClick={() => onUpdate(f.id, { card_approved: true })} data-testid={`firm-approve-card-${f.id}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border border-blue-400/50 text-blue-300 hover:bg-blue-400/10 transition-colors">
                  <Check className="w-3.5 h-3.5" /> Schváliť vizitku
                </button>
              )}
              {f.card_approved && (
                <button onClick={() => onUpdate(f.id, { card_approved: false })} data-testid={`firm-unpublish-card-${f.id}`}
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest border border-white/20 text-white/40 hover:border-white/40 transition-colors">
                  Skryť vizitku
                </button>
              )}
              {!f.unsubscribed && f.approved && (
                <button onClick={() => onUpdate(f.id, { featured: !f.featured })} data-testid={`firm-feature-${f.id}`} title={f.featured ? "Zrušiť topovanie" : "Topovať (priorita)"}
                  className={`p-1.5 border transition-colors ${f.featured ? "border-gold text-gold" : "border-white/20 text-white/40 hover:text-gold hover:border-gold/50"}`}>
                  <Star className={`w-4 h-4 ${f.featured ? "fill-gold" : ""}`} />
                </button>
              )}
              {!f.unsubscribed && f.approved && (
                <button onClick={() => onUpdate(f.id, { active: !f.active })} data-testid={`firm-toggle-${f.id}`}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border transition-colors ${f.active ? "border-green-400/40 text-green-400 hover:bg-green-400/10" : "border-white/20 text-white/40 hover:border-white/40"}`}>
                  {f.active ? "Aktívna" : "Neaktívna"}
                </button>
              )}
              <button onClick={() => setEditFirm(f)} data-testid={`firm-edit-${f.id}`} className="text-white/30 hover:text-gold transition-colors p-1.5"><Edit3 className="w-4 h-4" /></button>
              <button onClick={() => onDelete(f.id)} data-testid={`firm-delete-${f.id}`} className="text-white/30 hover:text-red-400 transition-colors p-1.5"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          );
        })}
        {list.length === 0 && <div className="text-white/40 text-sm py-10 text-center" data-testid="firm-empty">Zatiaľ žiadne firmy. Pridajte prvú vyššie alebo použite hromadný import.</div>}
      </div>

      {editFirm && (
        <FirmEditModal firm={editFirm} districts={districts} onClose={() => setEditFirm(null)}
          onSave={async (patch) => { await onUpdate(editFirm.id, patch); setEditFirm(null); toast.success("Firma upravená."); }} />
      )}
    </div>
  );
}

function FirmEditModal({ firm, districts, onClose, onSave }) {
  const [f, setF] = useState({
    company: firm.company || "", email: firm.email || "", phone: firm.phone || "",
    website: firm.website || "", country: firm.country || "SK", district: firm.district || "", city: firm.city || "",
  });
  const opts = districts?.[f.country] || [];
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="firm-edit-modal">
      <div className="bg-ink border border-gold/30 max-w-lg w-full p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl text-white">Upraviť firmu</h3>
          <button onClick={onClose} data-testid="firm-edit-close" className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid gap-3">
          <input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} placeholder="Názov firmy" data-testid="firm-edit-company" className="bg-ink-2 border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none" />
          <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="E-mail" data-testid="firm-edit-email" className="bg-ink-2 border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none" />
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Telefón" data-testid="firm-edit-phone" className="bg-ink-2 border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none" />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value, district: "" })} data-testid="firm-edit-country" className="bg-ink-2 border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none">
              <option value="SK">Slovensko</option><option value="CZ">Česko</option>
            </select>
            <select value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} data-testid="firm-edit-district" className="bg-ink-2 border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none">
              <option value="">Okres</option>
              {opts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="Web" data-testid="firm-edit-website" className="bg-ink-2 border border-white/15 px-3 py-2 text-white text-sm focus:border-gold outline-none" />
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={() => { if (!f.company || !f.email || !f.district) { toast.error("Názov, e-mail a okres sú povinné."); return; } onSave(f); }}
            data-testid="firm-edit-save" className="flex-1 bg-gold text-ink px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-gold-light transition-colors">Uložiť</button>
          <button onClick={onClose} className="px-5 py-3 text-white/50 hover:text-white text-xs uppercase tracking-widest">Zrušiť</button>
        </div>
      </div>
    </div>
  );
}



function BookingsTab({ bookings, onStatus, onDelete, blockedDates = [], onSaveBlocked }) {
  const STATUS = { new: "Nová", confirmed: "Potvrdená", done: "Vybavená", cancelled: "Zrušená" };
  const [newDate, setNewDate] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const addBlocked = () => {
    if (!newDate) return;
    if (blockedDates.includes(newDate)) { setNewDate(""); return; }
    onSaveBlocked([...blockedDates, newDate]);
    setNewDate("");
  };
  const removeBlocked = (d) => onSaveBlocked(blockedDates.filter((x) => x !== d));
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("sk-SK", { day: "numeric", month: "long", year: "numeric" }); } catch { return d; } };
  return (
    <div className="space-y-8" data-testid="bookings-tab">
      {/* Blocked dates management */}
      <section className="border border-gold/20 bg-ink-2 p-6" data-testid="blocked-dates-panel">
        <h2 className="font-display text-3xl mb-1">Blokované termíny</h2>
        <p className="text-white/40 text-sm mb-5">Dni, kedy nie ste k dispozícii na obhliadky. Klienti si ich nebudú môcť rezervovať.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="font-mono2 text-xs uppercase tracking-widest text-white/60 mb-2 block">Zablokovať deň</label>
            <input type="date" min={today} value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="blocked-date-input" className="bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-2 text-sm" />
          </div>
          <button onClick={addBlocked} data-testid="blocked-date-add" className="flex items-center gap-2 bg-gold text-ink px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-gold-light"><Plus className="w-4 h-4" /> Zablokovať</button>
        </div>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="blocked-dates-list">
          {blockedDates.length === 0 && <span className="text-white/40 text-sm">Žiadne blokované termíny.</span>}
          {blockedDates.map((d) => (
            <span key={d} className="flex items-center gap-2 px-3 py-1.5 border border-gold/30 bg-gold/5 text-gold text-xs" data-testid={`blocked-date-${d}`}>
              {fmtDate(d)}
              <button onClick={() => removeBlocked(d)} data-testid={`blocked-date-remove-${d}`} className="text-white/50 hover:text-white"><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
      </section>

      <div className="space-y-4">
      <h2 className="font-display text-4xl mb-2">Rezervácie obhliadok</h2>
      {bookings.length === 0 && <p className="text-white/40 border border-white/10 p-10 text-center">Zatiaľ žiadne rezervácie obhliadok.</p>}
      {bookings.map((b) => (
        <div key={b.id} className="border border-white/10 bg-ink-2 p-5" data-testid={`booking-${b.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-display text-2xl text-white">{b.name}</div>
              <div className="text-white/40 text-xs">{b.email} · {b.phone || "—"}</div>
              {b.address && <div className="text-white/50 text-xs mt-1">Adresa: {b.address}</div>}
              {b.note && <div className="text-white/40 text-xs mt-1">Poznámka: {b.note}</div>}
            </div>
            <div className="text-right">
              <div className="font-mono2 text-gold text-lg">{(() => { try { return new Date(b.date).toLocaleDateString("sk-SK", { day: "numeric", month: "long", year: "numeric" }); } catch { return b.date; } })()}</div>
              <div className="text-white/50 text-xs">{b.slot_label}</div>
              <select value={b.status} onChange={(e) => onStatus(b.id, e.target.value)} data-testid={`booking-status-${b.id}`} className="bg-ink border border-white/20 px-2 py-1 text-xs mt-2">
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button onClick={() => onDelete(b.id)} data-testid={`booking-delete-${b.id}`} className="mt-2 ml-2 inline-flex items-center gap-1 text-red-400/80 hover:text-red-400 hover:bg-red-500/10 border border-red-400/30 hover:border-red-400/60 px-2 py-1 text-xs transition-colors" title="Zmazať rezerváciu">
                <Trash2 className="w-3.5 h-3.5" /> Zmazať
              </button>
            </div>
          </div>
        </div>
      ))}
      </div>
    </div>
  );
}

function AdminsTab({ admins, onGrant, onRevoke }) {
  const [f, setF] = useState({ email: "", password: "", name: "" });
  const submit = async () => {
    if (!f.email) return toast.error("Zadajte e-mail.");
    const ok = await onGrant(f);
    if (ok) setF({ email: "", password: "", name: "" });
  };
  return (
    <div className="space-y-8" data-testid="admins-tab">
      <div>
        <h2 className="font-display text-4xl">Administrátori</h2>
        <p className="text-white/40 text-sm mt-1">Prideľte práva admina inému užívateľovi. Ak účet neexistuje, vytvorí sa nový (zadajte heslo).</p>
      </div>
      <section className="border border-white/10 p-6">
        <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-4">Pridať / povýšiť administrátora</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextField label="E-mail *" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="admin-grant-email" />
          <TextField label="Meno" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="admin-grant-name" />
          <TextField label="Heslo (pre nový účet)" value={f.password} onChange={(v) => setF({ ...f, password: v })} testid="admin-grant-password" />
        </div>
        <button onClick={submit} data-testid="admin-grant-btn" className="mt-4 flex items-center gap-2 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light"><ShieldCheck className="w-4 h-4" /> Prideliť práva admina</button>
      </section>
      <section className="border border-white/10">
        <div className="px-5 py-3 border-b border-white/10 font-mono2 text-xs uppercase tracking-widest text-gold">Súčasní administrátori</div>
        <div className="divide-y divide-white/5">
          {admins.map((a) => (
            <div key={a.id} className="flex items-center justify-between p-4" data-testid={`admin-row-${a.email}`}>
              <div><div className="text-white font-medium">{a.name || a.email}</div><div className="text-white/40 text-xs">{a.email}</div></div>
              <button onClick={() => onRevoke(a.id)} className="flex items-center gap-2 px-3 py-2 border border-white/20 hover:border-red-500 text-red-400 text-sm"><Trash2 className="w-4 h-4" /> Odobrať</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function NumField({ label, value, onChange, testid, step = "0.1" }) {
  return (<div><label className="block text-white/50 text-xs mb-1">{label}</label><input type="number" step={step} value={value} data-testid={testid} onChange={(e) => onChange(NUM(e.target.value))} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm font-mono2 focus:border-gold outline-none" /></div>);
}
function TextField({ label, value, onChange, testid }) {
  return (<div><label className="block text-white/50 text-xs mb-1">{label}</label><input value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-gold outline-none" /></div>);
}


function VisualizationsTab({ items, swatchUrl, setSwatchUrl, visLimits, setVisLimits, onSaveSwatch, onGallery }) {
  const [uploading, setUploading] = useState(false);
  const uploadPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Nahrajte súbor vo formáte PDF.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi().post("/upload", fd);
      setSwatchUrl(data.url);
      toast.success("PDF nahraté. Nezabudnite uložiť.");
    } catch { toast.error("Nahrávanie zlyhalo."); } finally { setUploading(false); }
  };
  return (
    <div className="space-y-8">
      <div className="border border-gold/30 bg-ink-2 p-6">
        <h3 className="font-display text-xl text-white mb-1">Vzorkovník Ceresit (PDF)</h3>
        <p className="text-white/50 text-sm mb-4">Nahrajte legálny PDF vzorkovník farieb. Zobrazí sa klientom na stránke vizualizácie na stiahnutie.</p>
        <div className="flex flex-col sm:flex-row gap-3 items-start">
          <input value={swatchUrl} onChange={(e) => setSwatchUrl(e.target.value)} data-testid="swatch-url"
            placeholder="URL PDF alebo nahrajte súbor" className="flex-1 w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-gold outline-none" />
          <label className="cursor-pointer border border-white/20 px-4 py-2 text-sm hover:border-gold whitespace-nowrap">
            {uploading ? "Nahrávam…" : "Nahrať PDF"}
            <input type="file" accept="application/pdf" className="hidden" data-testid="swatch-upload" onChange={(e) => uploadPdf(e.target.files?.[0])} />
          </label>
          <button onClick={onSaveSwatch} data-testid="swatch-save" className="bg-gold text-ink px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-gold-light">Uložiť</button>
        </div>
        {swatchUrl && <a href={imgUrl(swatchUrl)} target="_blank" rel="noreferrer" className="inline-block mt-3 text-gold text-sm underline">Otvoriť aktuálny vzorkovník</a>}
      </div>

      <div className="border border-white/10 bg-ink-2 p-6">
        <h3 className="font-display text-xl text-white mb-1">Denný limit bezplatných vizualizácií</h3>
        <p className="text-white/50 text-sm mb-4">AI generovanie je platené — nastavte, koľko vizualizácií môže vzniknúť za 24 hodín na jeden e-mail a jednu IP adresu.</p>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-white/50 text-xs mb-1">Max na e-mail / 24h</label>
            <input type="number" min="1" value={visLimits.per_email_limit} data-testid="vis-limit-email"
              onChange={(e) => setVisLimits({ ...visLimits, per_email_limit: e.target.value })}
              className="w-32 bg-transparent border border-white/15 px-3 py-2 text-sm font-mono2 text-gold focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1">Max na IP / 24h</label>
            <input type="number" min="1" value={visLimits.per_ip_limit} data-testid="vis-limit-ip"
              onChange={(e) => setVisLimits({ ...visLimits, per_ip_limit: e.target.value })}
              className="w-32 bg-transparent border border-white/15 px-3 py-2 text-sm font-mono2 text-gold focus:border-gold outline-none" />
          </div>
          <button onClick={onSaveSwatch} data-testid="vis-limit-save" className="bg-gold text-ink px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-gold-light">Uložiť nastavenia</button>
        </div>
      </div>

      <div>
        <h3 className="font-display text-xl text-white mb-4">Vizualizácie klientov ({items.length})</h3>
        {items.length === 0 && <p className="text-white/40 border border-white/10 p-10 text-center">Zatiaľ žiadne vizualizácie.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="visualizations-list">
          {items.map((v) => (
            <div key={v.id} className="border border-white/10 bg-ink-2 p-4" data-testid={`viz-${v.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-white font-semibold">{v.name || "—"}</p>
                  <a href={`mailto:${v.email}`} className="text-gold text-sm">{v.email}</a>
                </div>
                <span className="text-white/40 text-xs">{(v.created_at || "").slice(0, 10)}</span>
              </div>
              <p className="text-white/50 text-xs mb-3">Farby: {v.colors || v.palette || "—"}</p>
              <div className="grid grid-cols-4 gap-1">
                {v.original && <a href={imgUrl(v.original)} target="_blank" rel="noreferrer"><img src={imgUrl(v.original)} alt="orig" className="w-full h-16 object-cover opacity-70" /></a>}
                {(v.images || []).map((u, i) => (
                  <a key={i} href={imgUrl(u)} target="_blank" rel="noreferrer"><img src={imgUrl(u)} alt={`v${i}`} className="w-full h-16 object-cover" /></a>
                ))}
              </div>
              {v.gallery_consent && (
                <div className="mt-3 flex items-center justify-between gap-2 border-t border-white/10 pt-3" data-testid={`viz-gallery-row-${v.id}`}>
                  <span data-testid={`viz-gallery-status-${v.id}`}
                    className={`text-[11px] px-2 py-1 border font-mono2 uppercase tracking-wider ${
                      v.gallery_status === "approved" ? "border-green-500/50 text-green-400"
                      : v.gallery_status === "rejected" ? "border-red-500/50 text-red-400"
                      : "border-gold/50 text-gold"}`}>
                    {v.gallery_status === "approved" ? "V galérii" : v.gallery_status === "rejected" ? "Zamietnuté" : "Súhlas — čaká na schválenie"}
                  </span>
                  <div className="flex gap-2">
                    {v.gallery_status !== "approved" && (
                      <button onClick={() => onGallery(v.id, "approve")} data-testid={`viz-gallery-approve-${v.id}`}
                        className="flex items-center gap-1 bg-gold text-ink px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-gold-light">
                        <Check className="w-3.5 h-3.5" /> Do galérie
                      </button>
                    )}
                    {v.gallery_status !== "rejected" && (
                      <button onClick={() => onGallery(v.id, "reject")} data-testid={`viz-gallery-reject-${v.id}`}
                        className="flex items-center gap-1 border border-white/20 text-white/60 px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:border-red-400 hover:text-red-400">
                        <Ban className="w-3.5 h-3.5" /> {v.gallery_status === "approved" ? "Stiahnuť" : "Zamietnuť"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CouponsTab({ data }) {
  if (!data) return <p className="text-white/40">Načítavam…</p>;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {[["Kupóny", data.total], ["Uplatnení spolu", data.total_uses]].map(([l, v]) => (
          <div key={l} className="border border-white/10 bg-ink-2 p-6">
            <div className="text-white/50 text-xs uppercase tracking-widest">{l}</div>
            <div className="font-display text-4xl text-gold mt-2">{v}</div>
          </div>
        ))}
      </div>
      <div className="border border-white/10 overflow-x-auto" data-testid="coupons-list">
        <table className="w-full text-sm">
          <thead><tr className="text-white/40 text-xs uppercase tracking-widest border-b border-white/10">
            <th className="text-left p-3">Kód</th><th className="text-left p-3">Zľava</th><th className="text-left p-3">Typ</th>
            <th className="text-left p-3">E-mail</th><th className="text-left p-3">Použité</th><th className="text-left p-3">Vytvorené</th>
          </tr></thead>
          <tbody>
            {(data.coupons || []).map((c) => (
              <tr key={c.code} className="border-b border-white/5">
                <td className="p-3 font-mono2 text-gold">{c.code}</td>
                <td className="p-3 text-white/80">{c.percent}% {c.scope}</td>
                <td className="p-3 text-white/50">{c.kind}</td>
                <td className="p-3 text-white/50">{c.email || "—"}</td>
                <td className="p-3 text-white/80">{c.uses}</td>
                <td className="p-3 text-white/40">{(c.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmaTab() {
  const [convs, setConvs] = useState(null);
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    authApi().get("/admin/chatbot/conversations").then((r) => setConvs(r.data)).catch(() => setConvs([]));
  }, []);
  if (!convs) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-gold animate-spin" /></div>;
  return (
    <div data-testid="ema-tab">
      <h2 className="font-display text-2xl text-white mb-1">Ema — AI asistentka</h2>
      <p className="text-white/50 text-sm mb-6">Konverzácie návštevníkov s chatovou asistentkou Emou ({convs.length}).</p>
      {convs.length === 0 && <p className="text-white/40 text-sm" data-testid="ema-empty">Zatiaľ žiadne konverzácie.</p>}
      <div className="space-y-3">
        {convs.map((c) => (
          <div key={c.session_id} className="border border-white/10 bg-ink-2" data-testid={`ema-conv-${c.session_id.slice(0, 8)}`}>
            <button onClick={() => setOpenId(openId === c.session_id ? null : c.session_id)}
              className="w-full text-left p-4 flex items-center gap-4 hover:bg-white/5 transition-colors">
              <div className="w-9 h-9 bg-gold/15 text-gold flex items-center justify-center shrink-0"><MessageCircle className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-medium truncate">{(c.messages.find((m) => m.role === "user") || {}).content || "(bez správ)"}</p>
                <p className="text-white/40 text-xs mt-0.5">{c.messages.length} správ · {c.updated_at ? new Date(c.updated_at).toLocaleString("sk-SK") : ""}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${openId === c.session_id ? "rotate-180" : ""}`} />
            </button>
            {openId === c.session_id && (
              <div className="border-t border-white/10 p-4 space-y-3 max-h-96 overflow-y-auto" data-testid="ema-conv-detail">
                {c.messages.map((m, i) => (
                  <div key={i} className={`text-sm leading-relaxed ${m.role === "user" ? "text-white" : "text-white/60"}`}>
                    <span className={`font-mono2 text-[10px] uppercase tracking-widest block mb-0.5 ${m.role === "user" ? "text-gold" : "text-white/35"}`}>
                      {m.role === "user" ? "Návštevník" : "Ema"}
                    </span>
                    {m.content}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BotVisitsTab({ data }) {
  if (!data) return <p className="text-white/40">Načítavam…</p>;
  const max = Math.max(1, ...(data.days || []).map((d) => d.count));
  return (
    <div className="space-y-8">
      <div className="border border-white/10 bg-ink-2 p-6">
        <h3 className="font-display text-xl text-white mb-1">Návštevy crawlerov — vyhľadávače aj AI asistenti</h3>
        <p className="text-white/50 text-sm">Koľkokrát nás navštívili crawlery: Googlebot, Bingbot, SeznamBot aj AI asistenti (ChatGPT, Perplexity, Claude, Gemini). Vidíte tak, že vás vyhľadávače skutočne čítajú — prvý signál indexácie ešte pred dátami v Search Console. (Zaznamenávajú sa prístupy na API/sitemap/robots — detailnú per-page štatistiku ukáže Google Search Console.)</p>
        <div className="font-display text-5xl text-gold mt-4" data-testid="botvisits-total">{data.total}</div>
        <div className="text-white/40 text-xs uppercase tracking-widest">návštev spolu</div>
      </div>

      {data.by_bot?.length > 0 ? (
        <div className="border border-white/10 overflow-x-auto" data-testid="botvisits-list">
          <table className="w-full text-sm">
            <thead><tr className="text-white/40 text-xs uppercase tracking-widest border-b border-white/10">
              <th className="text-left p-3">AI bot</th><th className="text-left p-3">Návštevy</th>
              <th className="text-left p-3">Naposledy</th><th className="text-left p-3">Posledná stránka</th>
            </tr></thead>
            <tbody>
              {data.by_bot.map((b) => (
                <tr key={b.bot} className="border-b border-white/5">
                  <td className="p-3 text-white/90 font-semibold">{b.bot}</td>
                  <td className="p-3 text-gold font-mono2">{b.count}</td>
                  <td className="p-3 text-white/50">{b.last_seen ? new Date(b.last_seen).toLocaleString("sk-SK") : "—"}</td>
                  <td className="p-3 text-white/40 font-mono2 text-xs">{b.last_path || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-white/40 border border-white/10 p-10 text-center">Zatiaľ žiadne návštevy AI botov. Po indexácii sa tu objavia.</p>
      )}

      {data.days?.length > 0 && (
        <div className="border border-white/10 bg-ink-2 p-6">
          <p className="font-mono2 text-white/50 uppercase tracking-widest text-xs mb-4">Posledných 30 dní</p>
          <div className="flex items-end gap-1 h-32">
            {data.days.map((d) => (
              <div key={d.date} className="flex-1 bg-gold/70 hover:bg-gold transition-colors" style={{ height: `${(d.count / max) * 100}%` }} title={`${d.date}: ${d.count}`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function SubscribersTab({ data }) {
  const exportCsv = async () => {
    try {
      const res = await authApi().get("/admin/newsletter/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "odberatelia.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export zlyhal"); }
  };
  if (!data) return <p className="text-white/40">Načítavam…</p>;
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h3 className="font-display text-2xl text-white">Odberatelia noviniek</h3>
          <p className="text-white/50 text-sm">Spolu <span className="text-gold font-mono2" data-testid="subscribers-total">{data.total}</span> odberateľov</p>
        </div>
        <button onClick={exportCsv} data-testid="subscribers-export"
          className="bg-gold text-ink px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-gold-light flex items-center gap-2">
          <FileText className="w-4 h-4" /> Stiahnuť CSV
        </button>
      </div>
      {data.subscribers?.length ? (
        <div className="border border-white/10 overflow-x-auto" data-testid="subscribers-list">
          <table className="w-full text-sm">
            <thead><tr className="text-white/40 text-xs uppercase tracking-widest border-b border-white/10">
              <th className="text-left p-3">E-mail</th><th className="text-left p-3">Zdroj</th><th className="text-left p-3">Dátum</th>
            </tr></thead>
            <tbody>
              {data.subscribers.map((s) => (
                <tr key={s.id || s.email} className="border-b border-white/5">
                  <td className="p-3 text-white/90"><a href={`mailto:${s.email}`} className="hover:text-gold">{s.email}</a></td>
                  <td className="p-3 text-white/50">{s.source}</td>
                  <td className="p-3 text-white/40">{s.created_at ? new Date(s.created_at).toLocaleDateString("sk-SK") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-white/40 border border-white/10 p-10 text-center">Zatiaľ žiadni odberatelia.</p>
      )}
    </div>
  );
}

